// assets/caja-3d-viewer.js
import { Component } from '@theme/component';

/**
 * Line item property keys read from the product-custom-property blocks of the ammunition box template.
 * Keep them aligned with the `property_key` settings in templates/product.caja-municion.json.
 */
const PROPERTIES = {
  boxColor: 'Color de la caja',
  claspColor: 'Color del cierre',
  text: 'Iniciales o texto',
  textPosition: 'Posición del texto',
  textColor: 'Color del texto',
  animal: 'Animal u objeto',
  animalPosition: 'Posición del animal',
  animalColor: 'Color del animal',
  caliber: 'Calibre',
  caliberPosition: 'Posición del calibre',
  caliberColor: 'Color del calibre',
  flag: 'Bandera',
  flagPosition: 'Posición de la bandera',
  image: 'Imagen',
  imagePosition: 'Posición de la imagen',
  imageColor: 'Color de la imagen',
  textSize: 'Tamaño del texto',
  caliberSize: 'Tamaño del calibre',
  imageSize: 'Tamaño de la imagen',
  animalArt: 'Dibujo del animal',
  animalSize: 'Tamaño del animal',
  flagSize: 'Tamaño de la bandera',
  flagStyle: 'Estilo de la bandera',
};

/** Smallest printable letter height in mm. */
const MIN_TEXT_MM = 8;
/** Logo width in mm used when the customer has not picked one. */
const DEFAULT_LOGO_MM = 30;
/** Flag width in mm used when the customer has not picked one. */
const DEFAULT_FLAG_MM = 30;
/** Cap height of the heading font as a share of its font size, used to turn letter height into font size. */
const CAP_HEIGHT = 0.7;
/** Letters and digits that fit a text on boxes of up to 10 cartridges at the minimum letter height. */
const SMALL_BOX_MAX_CHARACTERS = 8;

/** Flag style value that picks the rectangular flag instead of the brush-stroke one. */
const CLASSIC_FLAG = /cl[aá]sica/i;
/** Code of the flag drawn with the shop's own brush-stroke and classic images instead of its SVG. */
const SPAIN_FLAG_CODE = 'es';
/** Flag style fields that only apply once a flag is chosen. */
const FLAG_DETAIL_KEYS = [PROPERTIES.flagStyle, PROPERTIES.flagSize, PROPERTIES.flagPosition];

/**
 * @typedef {object} FlagChoice
 * @property {string} name - Country or region, as sent with the order.
 * @property {string} code - Flag code, for example "de" or "es-an".
 * @property {string} src - URL of the flag image.
 * @property {string[]} colors - Main colors of the flag; each one counts as a filament color of its piece.
 */

/**
 * @param {HTMLOptionElement | null | undefined} option - An option of the Flag field (snippets/banderas.liquid).
 * @returns {FlagChoice | null}
 */
function flagFromOption(option) {
  if (!option?.value) return null;
  return {
    name: option.value,
    code: option.dataset.code ?? '',
    src: option.dataset.flagSrc ?? '',
    colors: (option.dataset.colors ?? '')
      .split(',')
      .map((color) => color.trim())
      .filter(Boolean),
  };
}

/**
 * @param {HTMLFormElement | null} form
 * @returns {HTMLSelectElement | null} The select of the Flag field.
 */
function getFlagSelect(form) {
  const select = getControls(form, PROPERTIES.flag)[0];
  return select instanceof HTMLSelectElement ? select : null;
}

/**
 * @param {HTMLFormElement | null} form
 * @returns {(name: string) => string[]} Colors of a flag by name, read from the Flag field options.
 */
function flagColorsIn(form) {
  /** @type {Map<string, string[]>} */
  const colors = new Map();
  for (const option of getFlagSelect(form)?.options ?? []) {
    const flag = flagFromOption(option);
    if (flag && !colors.has(flag.name)) colors.set(flag.name, flag.colors);
  }
  return (name) => colors.get(name) ?? [];
}
const NO_ANIMAL = /^sin\b/i;
/** The animal option for customers who upload their own one-colour drawing. */
const CUSTOM_ANIMAL = /^otro\b/i;
const CUSTOM_ANIMAL_LABEL = 'Tu animal';

/**
 * @param {string} name
 * @returns {string} The animal name without case, accents or spaces, so "Jabalí" matches `data-animal-art-jabali`.
 */
function animalKey(name) {
  return name
    .normalize('NFD')
    .replace(/[^a-z]/gi, '')
    .toLowerCase();
}
/** Stands in for the box color until the customer picks one, since it always takes a slot. */
const PENDING_BOX_COLOR = '__box';

const CAPACITY_OPTION = /tama|capacidad|bala|cartucho/i;
const CLASPS_OPTION = /cierre/i;
// Padlock option (any name with "candado"). Values: Sin candado / Preparada para candado /
// Preparada + candado de código / Solo candado de código, or simply Sí / No for the lugs
const LOCK_OPTION = /candado/i;
const LOCK_LUGS_VALUE = /prepar|^\s*s[ií]\s*$/i;
const PADLOCK_VALUE = /c[oó]digo/i;
const DEFAULT_CAPACITY = 20;
const SINGLE_CLASP_MAX_CAPACITY = 10;
const DEFAULT_COLORS = { box: '#A09D98', clasp: '#2B2B2B', engraving: '#2B2B2B' };
const LIGHT_ENGRAVING = '#F4F1EC';
const SAMPLE_TEXT = 'J.M.G.';
const SAMPLE_CALIBER = '.308 WIN';
// Desktop shows the preview sticky beside the fields; below it the preview floats over the page
const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

/** @type {Promise<void> | null} */
let threeLoading = null;

/**
 * Tells the personalization fields how much of the top of the screen the floating preview covers,
 * so a field with an error is scrolled into view below it (see `--personalization-scroll-offset`).
 * @param {Element | null} stage - The floating stage, or null when nothing floats.
 */
function setScrollOffset(stage) {
  const root = document.documentElement;
  if (stage) root.style.setProperty('--personalization-scroll-offset', `${Math.round(stage.getBoundingClientRect().bottom) + 16}px`);
  else root.style.removeProperty('--personalization-scroll-offset');
}

/**
 * @param {string | undefined} formId
 * @returns {HTMLFormElement | null}
 */
function getForm(formId) {
  const form = document.getElementById(formId ?? '');
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * Returns the controls of a line item property, including those linked with the `form` attribute.
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>}
 */
function getControls(form, key) {
  const field = form?.elements.namedItem(`properties[${key}]`);
  if (!field) return [];
  const items = field instanceof RadioNodeList ? [...field] : [field];
  return items.filter(
    (item) => item instanceof HTMLInputElement || item instanceof HTMLSelectElement || item instanceof HTMLTextAreaElement
  );
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {string} The trimmed value, the checked radio value, or an empty string.
 */
function readValue(form, key) {
  for (const control of getControls(form, key)) {
    if (control instanceof HTMLInputElement && control.type === 'radio') {
      if (control.checked) return control.value.trim();
      continue;
    }
    if (control instanceof HTMLInputElement && control.type === 'file') return control.files?.length ? 'file' : '';
    return control.value.trim();
  }
  return '';
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {File | null} The file chosen in a file upload property.
 */
function readFile(form, key) {
  const control = getControls(form, key)[0];
  return control instanceof HTMLInputElement && control.type === 'file' ? (control.files?.[0] ?? null) : null;
}

/**
 * Keeps one object URL for the latest uploaded image and revokes it when the file changes.
 */
class UploadedArt {
  /** @type {File | null} */
  #file = null;
  #url = '';

  /**
   * @param {File | null} file
   * @returns {string} An object URL for an image file, or an empty string.
   */
  urlFor(file) {
    if (!file || !file.type.startsWith('image/')) {
      this.release();
      return '';
    }
    if (file !== this.#file) {
      this.release();
      this.#file = file;
      this.#url = URL.createObjectURL(file);
    }
    return this.#url;
  }

  release() {
    if (this.#url) URL.revokeObjectURL(this.#url);
    this.#file = null;
    this.#url = '';
  }
}

/**
 * @param {HTMLFormElement | null} form
 * @param {string} key
 * @returns {string} The hex color of the checked swatch, or an empty string.
 */
function readSwatchColor(form, key) {
  for (const control of getControls(form, key)) {
    if (control instanceof HTMLInputElement && control.checked) return control.dataset.color ?? '';
  }
  return '';
}

/**
 * @param {EventTarget | null} target
 * @returns {string} The line item property key of a personalization control, or an empty string.
 */
function propertyKeyOf(target) {
  const name = target instanceof HTMLInputElement || target instanceof HTMLSelectElement ? target.name : '';
  return name.match(/^properties\[(.+)\]$/)?.[1] ?? '';
}

/**
 * @typedef {object} BoxLayout
 * @property {number} capacity
 * @property {1 | 2} clasps
 * @property {boolean} lockLugs - Lugs with a hole between the clasps for a padlock (two-clasp boxes only).
 * @property {boolean} padlock - A combination padlock comes with the box (two-clasp boxes only).
 */

/**
 * Reads the selected variant: capacity, number of clasps and padlock option.
 * @param {ParentNode} root - The product section.
 * @returns {BoxLayout}
 */
function readLayout(root) {
  let capacity = DEFAULT_CAPACITY;
  let clasps = 2;
  let lockLugs = false;
  let padlock = false;

  for (const element of root.querySelectorAll('[data-option-name]:checked')) {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLOptionElement)) continue;
    const name = element.dataset.optionName ?? '';
    if (LOCK_OPTION.test(name)) {
      lockLugs = LOCK_LUGS_VALUE.test(element.value);
      padlock = PADLOCK_VALUE.test(element.value);
      continue;
    }
    const number = parseInt(element.value, 10);
    if (Number.isNaN(number)) continue;
    if (CLASPS_OPTION.test(name)) clasps = number;
    else if (CAPACITY_OPTION.test(name)) capacity = number;
  }

  capacity = Math.min(Math.max(capacity, 1), 100);
  // Small boxes only have room for one clasp
  if (capacity <= SINGLE_CLASP_MAX_CAPACITY) clasps = 1;
  // The lugs are printed between the two clasps, and the padlock is only sold with them:
  // showing it beside a one-clasp box would suggest it comes included
  if (clasps === 1) {
    lockLugs = false;
    padlock = false;
  }
  return { capacity, clasps: clasps === 1 ? 1 : 2, lockLugs, padlock };
}

/**
 * @param {string} hex - A #RRGGBB color.
 * @returns {string} Dark engraving color for light boxes, light for dark ones.
 */
function contrastingColor(hex) {
  const rgb = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
  if (!rgb) return DEFAULT_COLORS.engraving;
  const [r, g, b] = rgb.slice(1).map((channel) => parseInt(channel, 16));
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? DEFAULT_COLORS.engraving : LIGHT_ENGRAVING;
}

/**
 * @param {string} value - For example "10 mm" or "8 mm (mínimo)".
 * @returns {number} Millimetres, or 0 when not set.
 */
function parseSize(value) {
  return parseInt(value, 10) || 0;
}

/**
 * At the 8 mm minimum letter height, small boxes only have room for short texts.
 * @param {number} capacity
 * @returns {number} Maximum letters and digits per text, or 0 for no limit.
 */
function maxCharactersFor(capacity) {
  return capacity <= SINGLE_CLASP_MAX_CAPACITY ? SMALL_BOX_MAX_CHARACTERS : 0;
}

/**
 * Counts letters and digits only; dots, spaces and dashes between initials do not take a slot.
 * @param {string} text
 */
function countCharacters(text) {
  return text.match(/[\p{L}\p{N}]/gu)?.length ?? 0;
}

/**
 * Loads three.js and OrbitControls once per page.
 * @param {string} threeSrc
 * @param {string} controlsSrc
 * @returns {Promise<void>}
 */
function loadThree(threeSrc, controlsSrc) {
  if (window.THREE?.OrbitControls) return Promise.resolve();
  threeLoading ??= loadScript(threeSrc).then(() => loadScript(controlsSrc));
  return threeLoading;
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.append(script);
  });
}

/**
 * @typedef {object} Caja3dViewerRefs
 * @property {HTMLElement} viewport - Hosts the canvas.
 * @property {HTMLCanvasElement} canvas - WebGL canvas.
 * @property {HTMLButtonElement} lidButton - Opens and closes the lid.
 * @property {HTMLButtonElement} cartridgesButton - Takes cartridges out or puts them back.
 * @property {HTMLButtonElement} spinButton - Toggles auto rotation.
 * @property {HTMLButtonElement[]} viewButtons - Quick camera views.
 * @property {HTMLButtonElement[]} modeButtons - Switch between the 3D preview and the product photos.
 * @property {HTMLElement} error - Message shown when the viewer cannot start.
 * @property {HTMLInputElement} [picture] - File field that sends a picture of the design with the order.
 */

/**
 * Interactive 3D preview of the ammunition box. It mirrors the personalization fields
 * and the selected variant (capacity and number of clasps) without owning any form data.
 * @extends Component<Caja3dViewerRefs>
 */
class Caja3dViewer extends Component {
  requiredRefs = ['viewport', 'canvas', 'lidButton', 'cartridgesButton', 'spinButton', 'error'];

  /** @type {BoxScene | null} */
  #scene = null;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {string} */
  #layoutKey = '';
  /** @type {string} */
  #engravingKey = '';

  #started = false;
  /** Whether the customer closed the floating preview. */
  #dismissed = false;
  /** Whether the viewer's place in the page is on screen. */
  #inView = true;
  /** @type {IntersectionObserver | null} */
  #floatObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const root = this.closest('.shopify-section') ?? document;
    const onFieldEvent = (/** @type {Event} */ event) => this.#handleFieldEvent(event);
    root.addEventListener('change', onFieldEvent, { signal });
    root.addEventListener('input', onFieldEvent, { signal });
    root.addEventListener('focusin', onFieldEvent, { signal });
    DESKTOP_QUERY.addEventListener('change', () => this.#updateFloating(), { signal });
    // The picture must be in the form before the cart reads it: the buy button is pressed before the
    // form is submitted (Enter in a field presses it too), and the theme reads the form on submit
    document.addEventListener('click', (event) => this.#attachPicture(event), { signal, capture: true });
    // Express checkout asks for the picture, and says when it has read the form
    document.addEventListener('personalization:picture', (event) => this.#attachPicture(event), { signal, capture: true });
    document.addEventListener('personalization:picture-sent', (event) => this.#clearPicture(event), { signal, capture: true });

    // On mobile the preview sits above the fields; once it scrolls away it floats at the top instead
    this.#floatObserver = new IntersectionObserver(([entry]) => {
      this.#inView = entry?.isIntersecting ?? true;
      this.#updateFloating();
    });
    this.#floatObserver.observe(this);

    if (this.dataset.mode === '3d') this.#ensureStarted();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#floatObserver?.disconnect();
    this.#floatObserver = null;
    this.#scene?.dispose();
    this.#scene = null;
    this.#started = false;
    this.#layoutKey = '';
    this.#engravingKey = '';
    this.#customArt.release();
  }

  /** Object URL of the drawing uploaded for a custom animal. */
  #customArt = new UploadedArt();

  /**
   * Sends a picture of the design with the order, so the workshop sees what the customer saw.
   * Only when the 3D preview was opened; otherwise the order goes without it.
   * @param {Event} event
   */
  #attachPicture(event) {
    const input = this.refs.picture;
    const form = getForm(this.dataset.formId);
    if (!input || !form) return;
    if (event.type === 'click') {
      const button = event.target instanceof Element ? event.target.closest('button[type="submit"], input[type="submit"]') : null;
      if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement) || button.form !== form) return;
      // The cart reads the form in the submit that follows this click; afterwards the picture is
      // removed, so a later express checkout never sends it as a bare file name
      setTimeout(() => (input.value = ''), 0);
    } else if (event.target !== form) {
      return;
    }
    const picture = this.#scene?.snapshot();
    if (picture) attachPicture(picture, input);
  }

  /**
   * Removes the picture once express checkout has read the form.
   * @param {Event} event
   */
  #clearPicture(event) {
    const input = this.refs.picture;
    if (input && event.target === getForm(this.dataset.formId)) input.value = '';
  }

  /**
   * Keeps the scene in sync and opens the 3D preview on desktop when a personalization field is used.
   * @param {Event} event
   */
  #handleFieldEvent(event) {
    if (event.type !== 'focusin') this.#sync(event);

    const target = event.target;
    // Compare form elements: the product form has an input named "id", which shadows `form.id`
    const isPersonalizationField =
      (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) &&
      target.name.startsWith('properties[') &&
      target.form !== null &&
      target.form === getForm(this.dataset.formId);
    if (isPersonalizationField && this.dataset.mode !== '3d' && !this.#dismissed) this.#setMode('3d');
  }

  /**
   * @param {'3d' | 'photos'} mode
   */
  #setMode(mode) {
    this.dataset.mode = mode;
    for (const modeButton of this.refs.modeButtons ?? []) {
      modeButton.setAttribute('aria-pressed', String(modeButton.dataset.mode === mode));
    }
    if (mode === '3d') this.#ensureStarted();
    this.#updateFloating();
  }

  /**
   * Floats the 3D stage at the top of a mobile screen while its place in the page is scrolled away.
   */
  #updateFloating() {
    const float = !this.#inView && this.dataset.mode === '3d' && !this.#dismissed && !DESKTOP_QUERY.matches;
    if (float === this.hasAttribute('data-floating')) return;
    // Keep the space the stage leaves behind so the page does not jump
    if (float) this.style.setProperty('--caja-3d-placeholder', `${this.offsetHeight}px`);
    this.toggleAttribute('data-floating', float);
    setScrollOffset(float ? this.querySelector('.caja-3d__stage') : null);
  }

  /**
   * Hides the floating preview; it stays closed until the customer picks 3D again.
   */
  dismissFloating() {
    this.#dismissed = true;
    this.#setMode('photos');
  }

  #ensureStarted() {
    if (this.#started || !this.#abortController) return;
    this.#started = true;
    this.#start(this.#abortController.signal);
  }

  /**
   * Drawings of the listed animals, from `data-animal-art-<name>` attributes.
   * @returns {Record<string, string>}
   */
  #animalArt() {
    /** @type {Record<string, string>} */
    const art = {};
    for (const [key, src] of Object.entries(this.dataset)) {
      if (key.startsWith('animalArt') && key.length > 9 && src) art[key.slice(9)] = src;
    }
    return art;
  }

  /**
   * @param {AbortSignal} signal
   */
  async #start(signal) {
    const { threeSrc = '', controlsSrc = '' } = this.dataset;

    try {
      await loadThree(threeSrc, controlsSrc);
    } catch {
      if (!signal.aborted) this.#showError();
      return;
    }
    if (signal.aborted) return;

    try {
      this.#scene = new BoxScene(this.refs.canvas, this.refs.viewport, {
        fontFamily: getComputedStyle(this).getPropertyValue('--font-heading--family').trim() || 'sans-serif',
        flagSrc: this.dataset.flagSrc ?? '',
        classicFlagSrc: this.dataset.classicFlagSrc ?? '',
        animalArt: this.#animalArt(),
        engravingFonts: {
          text: { family: 'Caja Cooper', src: this.dataset.textFontSrc ?? '' },
          caliber: { family: 'Caja Stencil', src: this.dataset.caliberFontSrc ?? '' },
        },
        onStateChange: () => this.#updateButtons(),
      });
    } catch {
      this.#showError();
      return;
    }

    this.#sync();
    document.fonts?.ready.then(() => {
      if (!signal.aborted) this.#scene?.redrawEngravings();
    });
  }

  #showError() {
    this.refs.error.hidden = false;
  }

  /**
   * Reads the variant and personalization fields and updates the scene.
   * @param {Event} [event]
   */
  #sync(event) {
    const scene = this.#scene;
    if (!scene) return;

    const layout = this.#readLayout();
    const layoutKey = JSON.stringify(layout);
    if (layoutKey !== this.#layoutKey) {
      const lockChanged =
        this.#layoutKey !== '' &&
        layoutKey.replace(/"(lockLugs|padlock)":\w+/g, '') === this.#layoutKey.replace(/"(lockLugs|padlock)":\w+/g, '');
      this.#layoutKey = layoutKey;
      scene.setLayout(layout);
      // Only the padlock option changed: show the front, where the lugs and the padlock are
      if (lockChanged) scene.goView('front');
    }

    const form = getForm(this.dataset.formId);
    scene.setColors({
      box: readSwatchColor(form, PROPERTIES.boxColor) || DEFAULT_COLORS.box,
      clasp: readSwatchColor(form, PROPERTIES.claspColor) || DEFAULT_COLORS.clasp,
    });

    const engravings = this.#readEngravings();
    const engravingKey = JSON.stringify(engravings);
    if (engravingKey === this.#engravingKey) return;

    const previous = this.#engravingKey ? /** @type {Engraving[]} */ (JSON.parse(this.#engravingKey)) : [];
    this.#engravingKey = engravingKey;
    scene.setEngravings(engravings);

    // Turn the camera towards the face the customer just changed
    if (!event) return;
    const changed = engravings.find((item, index) => JSON.stringify(item) !== JSON.stringify(previous[index]));
    if (changed) scene.goView(changed.face === 'lid' ? 'top' : 'front');
  }

  /**
   * @returns {BoxLayout}
   */
  #readLayout() {
    return readLayout(this.closest('.shopify-section') ?? document);
  }

  /**
   * @returns {Engraving[]}
   */
  #readEngravings() {
    const form = getForm(this.dataset.formId);
    const value = (/** @type {string} */ key) => readValue(form, key);
    // Until a color is picked, engravings use whichever of dark or light reads on the box
    const fallbackColor = contrastingColor(readSwatchColor(form, PROPERTIES.boxColor) || DEFAULT_COLORS.box);
    const color = (/** @type {string} */ key) => readSwatchColor(form, key) || fallbackColor;
    /** @type {Engraving[]} */
    const items = [];
    const text = value(PROPERTIES.text);
    const caliber = value(PROPERTIES.caliber);
    const animal = value(PROPERTIES.animal);
    const flag = flagFromOption(getFlagSelect(form)?.selectedOptions[0]);

    items.push({
      kind: 'text',
      value: text || SAMPLE_TEXT,
      sample: !text,
      font: 'text',
      size: parseSize(value(PROPERTIES.textSize)) || MIN_TEXT_MM,
      ...parsePosition(value(PROPERTIES.textPosition), DEFAULT_POSITIONS.text),
      color: color(PROPERTIES.textColor),
    });
    items.push({
      kind: 'text',
      value: caliber || SAMPLE_CALIBER,
      sample: !caliber,
      font: 'caliber',
      size: parseSize(value(PROPERTIES.caliberSize)) || MIN_TEXT_MM,
      ...parsePosition(value(PROPERTIES.caliberPosition), DEFAULT_POSITIONS.caliber),
      color: color(PROPERTIES.caliberColor),
    });
    if (animal && !NO_ANIMAL.test(animal)) {
      const custom = CUSTOM_ANIMAL.test(animal);
      const art = custom ? this.#customArt.urlFor(readFile(form, PROPERTIES.animalArt)) : '';
      items.push({
        kind: 'animal',
        value: custom ? CUSTOM_ANIMAL_LABEL : animal,
        sample: false,
        size: parseSize(value(PROPERTIES.animalSize)),
        ...parsePosition(value(PROPERTIES.animalPosition), DEFAULT_POSITIONS.animal),
        color: color(PROPERTIES.animalColor),
        ...(art ? { art } : {}),
      });
    }
    if (flag) {
      items.push({
        kind: 'flag',
        value: CLASSIC_FLAG.test(value(PROPERTIES.flagStyle)) ? 'classic' : '',
        sample: false,
        size: parseSize(value(PROPERTIES.flagSize)) || DEFAULT_FLAG_MM,
        ...parsePosition(value(PROPERTIES.flagPosition), DEFAULT_POSITIONS.flag),
        color: '',
        code: flag.code,
        art: flag.src,
      });
    }
    if (value(PROPERTIES.image)) {
      // The uploaded file is only a reference, so the preview marks where and how wide it goes
      items.push({
        kind: 'label',
        value: 'LOGO',
        sample: false,
        size: parseSize(value(PROPERTIES.imageSize)) || DEFAULT_LOGO_MM,
        ...parsePosition(value(PROPERTIES.imagePosition), DEFAULT_POSITIONS.image),
        color: color(PROPERTIES.imageColor),
      });
    }

    return items;
  }

  #updateButtons() {
    const scene = this.#scene;
    if (!scene) return;

    const { lidButton, cartridgesButton, spinButton } = this.refs;
    const { openLid = '', closeLid = '', takeOut = '', putBack = '' } = this.dataset;
    lidButton.textContent = scene.lidOpen ? closeLid : openLid;
    lidButton.disabled = scene.lidBusy;
    cartridgesButton.textContent = scene.cartridgesOut ? putBack : takeOut;
    spinButton.setAttribute('aria-pressed', String(scene.spinning));
    for (const button of this.refs.viewButtons ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.view === scene.currentView));
    }
  }

  toggleLid() {
    this.#scene?.toggleLid();
  }

  toggleCartridges() {
    this.#scene?.toggleCartridges();
  }

  toggleSpin() {
    this.#scene?.toggleSpin();
  }

  /**
   * Shows either the 3D preview or the product photos. CSS hides the inactive one.
   * @param {Event} event - Click on a mode button.
   */
  showMode(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-mode]') : null;
    const mode = button instanceof HTMLElement ? button.dataset.mode : undefined;
    if (mode !== '3d' && mode !== 'photos') return;
    // Choosing 3D again lets the preview float and open automatically once more
    if (mode === '3d') this.#dismissed = false;
    this.#setMode(mode);
  }

  /**
   * @param {Event} event - Click on a quick view button.
   */
  showView(event) {
    // Declarative events are delegated, so read the button from the event target
    const button = event.target instanceof Element ? event.target.closest('[data-view]') : null;
    const view = button instanceof HTMLElement ? button.dataset.view : undefined;
    if (view) this.#scene?.goView(view);
  }
}

/**
 * @typedef {object} Placement
 * @property {'lid' | 'front'} face - The lid is printed as one piece, the front belongs to the base.
 * @property {-1 | 0 | 1} col - Left, center or right.
 * @property {-1 | 0 | 1} row - Top, middle or bottom. Side columns use top or bottom.
 */

/**
 * @typedef {Placement & { kind: 'text' | 'animal' | 'flag' | 'label', value: string, sample: boolean, size: number, color: string, font?: 'text' | 'caliber', art?: string, code?: string }} Engraving
 * `sample` marks placeholder text the customer has not typed. `size` is the text cap height or the
 * logo width in mm, or 0 when the artwork fills its slot. `font` picks the engraving typeface:
 * Cooper style for texts and stencil for the caliber.
 */

/** @type {Record<string, Placement>} */
const DEFAULT_POSITIONS = {
  text: { face: 'lid', col: -1, row: 0 },
  caliber: { face: 'lid', col: 1, row: 0 },
  animal: { face: 'front', col: 0, row: 0 },
  image: { face: 'lid', col: 0, row: 0 },
  flag: { face: 'front', col: -1, row: 0 },
};

/**
 * Turns "Tapa – izquierda arriba" style values into a placement.
 * @param {string} value
 * @param {Placement} fallback
 * @returns {Placement}
 */
function parsePosition(value, fallback) {
  const text = value.toLowerCase();
  let { face, col, row } = fallback;
  if (text.includes('frontal')) face = 'front';
  else if (text.includes('tapa')) face = 'lid';
  if (text.includes('izquierda')) col = -1;
  else if (text.includes('derecha')) col = 1;
  else if (text.includes('centro')) col = 0;
  if (text.includes('arriba')) row = -1;
  else if (text.includes('abajo')) row = 1;
  else if (text) row = 0;
  return { face, col, row };
}

/**
 * Counts the filament colors each printed piece would need. The box color takes a slot on both
 * pieces; clasps are printed separately and do not count.
 * @param {Record<string, string>} values - Swatch names and select values keyed by property.
 * @param {(name: string) => string[]} flagColors - Colors of a flag by name.
 * @returns {{ lid: number, front: number }}
 */
function countPieceColors(values, flagColors) {
  const box = values[PROPERTIES.boxColor] || PENDING_BOX_COLOR;
  const pieces = { lid: new Set([box]), front: new Set([box]) };
  const animal = values[PROPERTIES.animal] ?? '';
  const items = [
    [values[PROPERTIES.textColor], values[PROPERTIES.textPosition], DEFAULT_POSITIONS.text],
    [values[PROPERTIES.caliberColor], values[PROPERTIES.caliberPosition], DEFAULT_POSITIONS.caliber],
    [animal && !NO_ANIMAL.test(animal) ? values[PROPERTIES.animalColor] : '', values[PROPERTIES.animalPosition], DEFAULT_POSITIONS.animal],
    [values[PROPERTIES.imageColor], values[PROPERTIES.imagePosition], DEFAULT_POSITIONS.image],
  ];
  for (const [color, position, fallback] of items) {
    if (color) pieces[parsePosition(position ?? '', fallback).face].add(color);
  }
  // Each main color of the flag takes a slot; one the piece already uses does not add another
  const flag = values[PROPERTIES.flag];
  if (flag) {
    const face = parsePosition(values[PROPERTIES.flagPosition] ?? '', DEFAULT_POSITIONS.flag).face;
    for (const color of flagColors(flag)) pieces[face].add(color);
  }
  return { lid: pieces.lid.size, front: pieces.front.size };
}

/** Properties whose choice can change how many colors a piece needs. */
const COLOR_LIMIT_KEYS = [
  PROPERTIES.boxColor,
  PROPERTIES.textColor,
  PROPERTIES.textPosition,
  PROPERTIES.caliberColor,
  PROPERTIES.caliberPosition,
  PROPERTIES.animal,
  PROPERTIES.animalColor,
  PROPERTIES.animalPosition,
  PROPERTIES.flag,
  PROPERTIES.flagPosition,
  PROPERTIES.imageColor,
  PROPERTIES.imagePosition,
];

/**
 * @typedef {object} CajaColorLimitRefs
 * @property {HTMLElement} lidCount - Shows the colors used on the lid.
 * @property {HTMLElement} frontCount - Shows the colors used on the base.
 * @property {HTMLDialogElement} dialog - Explains why a choice was undone.
 * @property {HTMLElement} dialogMessage - Message inside the dialog.
 * @property {HTMLElement} [notice] - Says which variant option was changed to reach a combination that exists.
 * @property {HTMLDialogElement} [rulesDialog] - The merchant's ordering rules, when they wrote some.
 */

/**
 * Applies the printing rules of the ammunition box: at most `data-max` filament colors per piece,
 * short texts on small boxes, a warning for small logos, and only size / clasp / padlock
 * combinations that can be bought.
 * @extends Component<CajaColorLimitRefs>
 */
class CajaColorLimit extends Component {
  requiredRefs = ['lidCount', 'frontCount', 'dialog', 'dialogMessage'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** Last choices that respected the color limit, used to undo a choice that breaks it. */
  #accepted = /** @type {Record<string, string>} */ ({});
  /** @type {Map<string, HTMLElement>} */
  #notices = new Map();
  /** True while this component selects variant options itself, so those changes are not corrected again. */
  #adjusting = false;
  /** True while a rejected choice is being undone, so the explanation just shown stays. */
  #reverting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const root = this.closest('.shopify-section') ?? document;
    root.addEventListener('change', (event) => this.#handleChange(event), { signal });
    root.addEventListener('input', () => this.#checkSizes(), { signal });
    document.fonts?.ready.then(() => {
      if (!signal.aborted) this.#checkSizes();
    });

    const form = getForm(this.dataset.formId);
    if (form) this.#accepted = this.#readValues(form);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    for (const notice of this.#notices.values()) notice.remove();
    this.#notices.clear();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  closeDialog() {
    this.refs.dialog.close();
  }

  openRules() {
    const { rulesDialog } = this.refs;
    if (rulesDialog && !rulesDialog.open) rulesDialog.showModal();
  }

  closeRules() {
    this.refs.rulesDialog?.close();
  }

  /**
   * Keeps the variant option the customer just picked and changes the others to the closest
   * combination that can be bought; an impossible combination would leave the buy button disabled.
   * @param {EventTarget | null} target
   */
  #fixCombination(target) {
    if (this.#adjusting) return;
    const chosen = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    if (!(chosen instanceof HTMLInputElement || chosen instanceof HTMLOptionElement) || !chosen.dataset.optionName) return;
    const root = this.closest('.shopify-section');
    const names = (this.dataset.optionNames ?? '').split('|');
    const combinations = (this.dataset.variantTitles ?? '')
      .split('|')
      .filter(Boolean)
      .map((title) => title.split(' / '));
    const index = names.indexOf(chosen.dataset.optionName);
    if (!root || index < 0 || !combinations.length) return;

    const options = /** @type {Array<HTMLInputElement | HTMLOptionElement>} */ (
      [...root.querySelectorAll('[data-option-name]')].filter(
        (element) => element instanceof HTMLInputElement || element instanceof HTMLOptionElement
      )
    );
    const isChosen = (/** @type {HTMLInputElement | HTMLOptionElement} */ option) =>
      option instanceof HTMLInputElement ? option.checked : option.selected;
    const selected = names.map((name) => options.find((option) => option.dataset.optionName === name && isChosen(option))?.value ?? '');
    const same = (/** @type {string[]} */ values) => values.every((value, i) => value === selected[i]);

    let notice = '';
    if (!combinations.some(same)) {
      let best = /** @type {string[] | null} */ (null);
      let fewestChanges = Infinity;
      for (const combination of combinations) {
        if (combination[index] !== selected[index]) continue;
        const changes = combination.filter((value, i) => value !== selected[i]).length;
        if (changes < fewestChanges) {
          best = combination;
          fewestChanges = changes;
        }
      }
      if (!best) return;

      const template = this.dataset.combinationAdjusted ?? '';
      const changed = [];
      this.#adjusting = true;
      try {
        for (const [i, value] of best.entries()) {
          if (value === selected[i]) continue;
          const option = options.find((element) => element.dataset.optionName === names[i] && element.value === value);
          if (option instanceof HTMLInputElement) {
            option.click();
          } else if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
            option.parentElement.value = value;
            option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            continue;
          }
          changed.push(template.replace('[option]', names[i] ?? '').replace('[value]', value));
        }
      } finally {
        this.#adjusting = false;
      }
      notice = changed.join(' ');
    }
    this.#showCombinationNotice(chosen, notice);
  }

  /**
   * Explains a changed variant option right below the variant options, where the customer is
   * looking, or in the rules block when the options are elsewhere.
   * @param {Element} chosen - The option the customer picked.
   * @param {string} text
   */
  #showCombinationNotice(chosen, text) {
    const picker = chosen.closest('variant-picker');
    if (!picker) {
      if (this.refs.notice && this.refs.notice.textContent !== text) this.refs.notice.textContent = text;
      return;
    }
    let notice = this.#notices.get('combination');
    if (!notice || notice.previousElementSibling !== picker) {
      notice?.remove();
      notice = document.createElement('p');
      notice.className = 'caja-notice caja-notice--warning';
      notice.setAttribute('role', 'status');
      picker.after(notice);
      this.#notices.set('combination', notice);
    }
    if (notice.textContent !== text) notice.textContent = text;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Record<string, string>}
   */
  #readValues(form) {
    /** @type {Record<string, string>} */
    const values = {};
    for (const key of COLOR_LIMIT_KEYS) values[key] = readValue(form, key);
    return values;
  }

  /**
   * Undoes a choice that would exceed the color limit and explains why.
   * @param {Event} event
   */
  #handleChange(event) {
    this.#fixCombination(event.target);
    const form = getForm(this.dataset.formId);
    if (!form) return;

    const key = propertyKeyOf(event.target);
    const target = event.target;
    if (
      COLOR_LIMIT_KEYS.includes(key) &&
      (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) &&
      target.form === form
    ) {
      const values = this.#readValues(form);
      const counts = countPieceColors(values, flagColorsIn(form));
      const max = this.#max();
      const flagChange = key === PROPERTIES.flag || key === PROPERTIES.flagPosition;
      if (counts.lid > max || counts.front > max) {
        const piece = counts.lid > max ? 'lid' : 'front';
        if (flagChange) this.#showFlagNotice(form, values, piece, max);
        else this.#showDialog(piece, max);
        // The undo dispatches its own change event, which updates the counts and the preview
        this.#revert(form, key, target);
        return;
      }
      if (flagChange && !this.#reverting) this.#setNotice('flag', getFlagSelect(form) ?? undefined, '', 'error');
      this.#accepted = values;
    }
    this.#update();
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} key
   * @param {HTMLInputElement | HTMLSelectElement} target
   */
  #revert(form, key, target) {
    const previous = this.#accepted[key] ?? '';
    /** @type {HTMLElement} */
    let source = form;
    for (const control of getControls(form, key)) {
      if (control instanceof HTMLSelectElement) {
        control.value = previous;
        source = control;
      } else if (control instanceof HTMLInputElement && control.type === 'radio') {
        control.checked = control.value === previous;
        if (control.checked) source = control;
      }
    }
    if (source === form) {
      // Nothing was chosen before: clear the swatch name shown next to the heading
      const selectedName = target.closest('product-custom-property-component')?.querySelector('[ref="selectedValue"]');
      if (selectedName) selectedName.textContent = '';
    }
    this.#reverting = true;
    try {
      source.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      this.#reverting = false;
    }
  }

  /**
   * @param {'lid' | 'front'} piece
   * @param {number} max
   */
  #showDialog(piece, max) {
    const { dialog, dialogMessage } = this.refs;
    const template = (piece === 'lid' ? this.dataset.dialogLid : this.dataset.dialogBase) ?? '';
    dialogMessage.textContent = template.replace('[max]', String(max));
    if (!dialog.open) dialog.showModal();
  }

  /**
   * Says, under the Flag field, that the chosen flag has too many colors for its piece.
   * @param {HTMLFormElement} form
   * @param {Record<string, string>} values - The rejected choices.
   * @param {'lid' | 'front'} piece
   * @param {number} max
   */
  #showFlagNotice(form, values, piece, max) {
    const name = values[PROPERTIES.flag] ?? '';
    const template = (piece === 'lid' ? this.dataset.flagLid : this.dataset.flagBase) ?? '';
    const message = template
      .replace('[flag]', name)
      .replace('[count]', String(flagColorsIn(form)(name).length))
      .replace('[max]', String(max));
    this.#setNotice('flag', getFlagSelect(form) ?? undefined, message, 'error');
  }

  #max() {
    return Number(this.dataset.max) || 4;
  }

  #update() {
    const form = getForm(this.dataset.formId);
    if (!form) return;
    const max = this.#max();
    const values = this.#readValues(form);
    const flagColors = flagColorsIn(form);

    // Mark the choices that would push a piece over the limit; picking one opens the dialog
    for (const key of COLOR_LIMIT_KEYS) {
      for (const control of getControls(form, key)) {
        const options =
          control instanceof HTMLSelectElement
            ? [...control.options]
            : control instanceof HTMLInputElement && control.type === 'radio'
              ? [control]
              : [];
        for (const option of options) {
          if (!option.value) continue;
          const chosen = option instanceof HTMLOptionElement ? option.selected : option.checked;
          const counts = countPieceColors({ ...values, [key]: option.value }, flagColors);
          const suffix = key === PROPERTIES.flag ? this.dataset.flagFull : this.dataset.optionFull;
          this.#markOption(option, !chosen && (counts.lid > max || counts.front > max), suffix);
        }
      }
    }

    const counts = countPieceColors(values, flagColors);
    this.#setCount(this.refs.lidCount, this.dataset.lidTemplate, counts.lid, max);
    this.#setCount(this.refs.frontCount, this.dataset.frontTemplate, counts.front, max);
    this.#checkSizes();
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} option
   * @param {boolean} full
   * @param {string} [suffix] - Added to the option label while it is full.
   */
  #markOption(option, full, suffix = this.dataset.optionFull ?? '') {
    option.toggleAttribute('data-color-limit', full);
    if (!(option instanceof HTMLOptionElement)) return;
    option.dataset.label ??= option.text;
    const label = option.dataset.label;
    const text = full ? `${label} ${suffix}` : label;
    if (option.text !== text) option.text = text;
  }

  /**
   * Blocks texts too long for small boxes and warns about small logos.
   */
  #checkSizes() {
    const form = getForm(this.dataset.formId);
    if (!form) return;
    const { capacity } = readLayout(this.closest('.shopify-section') ?? document);
    const maxCharacters = maxCharactersFor(capacity);

    for (const textKey of [PROPERTIES.text, PROPERTIES.caliber]) {
      const input = getControls(form, textKey)[0];
      if (!input) continue;
      const text = readValue(form, textKey);
      const message =
        maxCharacters && countCharacters(text) > maxCharacters
          ? (this.dataset.textTooLong ?? '').replace('[max]', String(maxCharacters))
          : '';
      input.setCustomValidity(message);
      const notice = this.#setNotice(textKey, input, message, 'error');
      if (notice && message) input.setAttribute('aria-describedby', notice.id);
      else input.removeAttribute('aria-describedby');
    }

    // "Otro" animal needs the customer's drawing
    const artInput = getControls(form, PROPERTIES.animalArt)[0];
    if (artInput instanceof HTMLInputElement) {
      const missing = CUSTOM_ANIMAL.test(readValue(form, PROPERTIES.animal)) && !readFile(form, PROPERTIES.animalArt);
      const message = missing ? this.dataset.animalArtMissing ?? '' : '';
      artInput.setCustomValidity(message);
      this.#setNotice(PROPERTIES.animalArt, artInput, message, 'error');
    }

    const logoMin = Number(this.dataset.logoMin) || DEFAULT_LOGO_MM;
    const logoSize = parseSize(readValue(form, PROPERTIES.imageSize));
    const logoAnchor = getControls(form, PROPERTIES.imageSize)[0] ?? getControls(form, PROPERTIES.image)[0];
    const logoMessage =
      readValue(form, PROPERTIES.image) && logoSize && logoSize < logoMin
        ? (this.dataset.logoSmall ?? '').replace('[size]', String(logoSize)).replace('[min]', String(logoMin))
        : '';
    this.#setNotice(PROPERTIES.imageSize, logoAnchor, logoMessage, 'warning');
  }

  /**
   * Shows a message at the end of the personalization field that owns `control`.
   * @param {string} id
   * @param {Element | undefined} control
   * @param {string} message
   * @param {'error' | 'warning'} tone
   * @returns {HTMLElement | null}
   */
  #setNotice(id, control, message, tone) {
    const host = control?.closest('product-custom-property-component, caja-bandera');
    if (!host) return null;
    let notice = this.#notices.get(id);
    if (!notice || notice.parentElement !== host) {
      notice?.remove();
      notice = document.createElement('p');
      notice.id = `CajaNotice-${this.id || 'box'}-${this.#notices.size}`;
      notice.className = `caja-notice caja-notice--${tone}`;
      notice.setAttribute('role', 'status');
      host.append(notice);
      this.#notices.set(id, notice);
    }
    if (notice.textContent !== message) notice.textContent = message;
    return notice;
  }

  /**
   * @param {HTMLElement} element
   * @param {string | undefined} template
   * @param {number} count
   * @param {number} max
   */
  #setCount(element, template = '', count, max) {
    const text = template.replace('[count]', String(count)).replace('[max]', String(max));
    if (element.textContent !== text) element.textContent = text;
    element.toggleAttribute('data-full', count >= max);
  }
}

/* ------------------------------------------------------------------------------------------------
 * 3D scene. Geometry and animation adapted from the original portacartuchos viewer.
 * Measurements in mm. Modelling space: x right, y back, z up (converted to Y-up when built).
 * ---------------------------------------------------------------------------------------------- */

const R = 9;
const HB = 64;
const HL = 32;
const WALL = 2.5;
const INS_TOP = HB - 3.5;
const PIN_R = 2.4;
const KN_R = 4.2;
const ZB = HB - 13.5;
const ZP = HB + 13;
const HEAD_UP = 18;
const CART_HALF = 42.4;
const PITCH_X = 17.4;
const PITCH_Y = 21.2;
const RC = PIN_R + 0.25 + 1.5;
const LIFT = 150;
// Clasp fins stick out this far (mm) from the front wall
const FIN_REACH = 9.5;
// Padlock shelves, one on the lid and one on the base, between the inner clasp fins. They meet at the
// seam and share a vertical hole for the shackle.
const LOCK_FRAME_DEPTH = 9;
const LOCK_SHELF = 6;
// Front engravings stay below the clasps
const FRONT_CENTER = 21.5;
const FRONT_HEIGHT = 34;

let W = 200;
let D = 62;
let Y0 = -D / 2;
let YH = D / 2 + 4.6;
let YR = Y0 - 5.6;
/** @type {number[]} */
let XS = [];
/** @type {number[]} */
let YS = [];

/**
 * Sets the module-level box dimensions for a cartridge grid.
 * @param {number} cols
 * @param {number} rows
 */
function applyGrid(cols, rows) {
  W = cols * PITCH_X + 26;
  D = rows * PITCH_Y + 19.6;
  Y0 = -D / 2;
  YH = D / 2 + 4.6;
  YR = Y0 - 5.6;
  XS = Array.from({ length: cols }, (_, i) => (i - (cols - 1) / 2) * PITCH_X);
  YS = Array.from({ length: rows }, (_, i) => (i - (rows - 1) / 2) * PITCH_Y);
}

/**
 * @param {number} capacity
 * @returns {[number, number]} Columns and rows.
 */
function gridFor(capacity) {
  if (capacity <= 5) return [capacity, 1];
  if (capacity <= 20) return [Math.ceil(capacity / 2), 2];
  return [10, Math.ceil(capacity / 10)];
}

/** @param {number} d */
function rad(d) {
  return (d * Math.PI) / 180;
}

/** @returns {number[][]} */
function arc(cx, cz, r, a0, a1, n) {
  const p = [];
  for (let i = 0; i <= n; i++) {
    const a = rad(a0 + ((a1 - a0) * i) / n);
    p.push([cx + r * Math.cos(a), cz + r * Math.sin(a)]);
  }
  return p;
}

function dedupe(pts, eps = 1e-6) {
  const o = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i];
    const q = o[o.length - 1];
    if (Math.hypot(p[0] - q[0], p[1] - q[1]) > eps) o.push(p);
  }
  return o;
}

function densify(pts, eps = 0.1, minlen = 0.6) {
  pts = dedupe(pts);
  const o = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const [a, b] = pts[i - 1];
    const [c, d] = pts[i];
    const L = Math.hypot(c - a, d - b);
    if (L > minlen) {
      const t = eps / L;
      o.push([a + (c - a) * t, b + (d - b) * t]);
      o.push([c - (c - a) * t, d - (d - b) * t]);
    }
    o.push(pts[i]);
  }
  return o;
}

function rrect(hw, hd, r, nArc = 10, eps = 0.12) {
  r = Math.max(r, 0.3);
  const cs = [
    [hw - r, hd - r, 0],
    [-hw + r, hd - r, 90],
    [-hw + r, -hd + r, 180],
    [hw - r, -hd + r, 270],
  ];
  const p = [];
  for (let k = 0; k < 4; k++) {
    const [cx, cy, a0] = cs[k];
    for (let i = 0; i <= nArc; i++) {
      const a = rad(a0 + (90 * i) / nArc);
      p.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    const [nx, ny, nd] = cs[(k + 1) % 4];
    const na = rad(nd);
    const p0 = p[p.length - 1];
    const p1 = [nx + r * Math.cos(na), ny + r * Math.sin(na)];
    const L = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
    p.push([p0[0] + ((p1[0] - p0[0]) * eps) / L, p0[1] + ((p1[1] - p0[1]) * eps) / L]);
    p.push([p1[0] - ((p1[0] - p0[0]) * eps) / L, p1[1] - ((p1[1] - p0[1]) * eps) / L]);
  }
  return p;
}

function signedArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

function ensureCCW(poly) {
  return signedArea(poly) > 0 ? poly : poly.slice().reverse();
}

function insetAll(poly, d) {
  const n = poly.length;
  const o = [];
  for (let i = 0; i < n; i++) {
    const a = poly[(i - 1 + n) % n];
    const b = poly[i];
    const c = poly[(i + 1) % n];
    const t1x = b[0] - a[0];
    const t1y = b[1] - a[1];
    const l1 = Math.hypot(t1x, t1y) || 1;
    const t2x = c[0] - b[0];
    const t2y = c[1] - b[1];
    const l2 = Math.hypot(t2x, t2y) || 1;
    const n1x = -t1y / l1;
    const n1y = t1x / l1;
    const n2x = -t2y / l2;
    const n2y = t2x / l2;
    let mx = n1x + n2x;
    let my = n1y + n2y;
    let lm = Math.hypot(mx, my);
    if (lm < 1e-6) {
      mx = n1x;
      my = n1y;
      lm = 1;
    }
    mx /= lm;
    my /= lm;
    const cosh = Math.max(0.35, mx * n1x + my * n1y);
    o.push([b[0] + (mx * d) / cosh, b[1] + (my * d) / cosh]);
  }
  return o;
}

function offsetPath(center, half) {
  const N = center.length;
  const left = [];
  const right = [];
  const normal = (i) => {
    let a;
    let b;
    if (i === 0) [a, b] = [center[0], center[1]];
    else if (i === N - 1) [a, b] = [center[N - 2], center[N - 1]];
    else [a, b] = [center[i - 1], center[i + 1]];
    const dy = b[0] - a[0];
    const dz = b[1] - a[1];
    const L = Math.hypot(dy, dz);
    return [-dz / L, dy / L];
  };
  for (let i = 0; i < N; i++) {
    const nn = normal(i);
    const p = center[i];
    left.push([p[0] + nn[0] * half, p[1] + nn[1] * half]);
    right.push([p[0] - nn[0] * half, p[1] - nn[1] * half]);
  }
  const cap = (p, q) => {
    const a0 = Math.atan2(q[1] - p[1], q[0] - p[0]);
    const o = [];
    for (let s = 1; s < 8; s++) {
      const a = a0 - (s * Math.PI) / 8;
      o.push([p[0] + half * Math.cos(a), p[1] + half * Math.sin(a)]);
    }
    return o;
  };
  return left.concat(cap(center[N - 1], left[N - 1]), right.slice().reverse(), cap(center[0], right[0]));
}

function buildGeom(verts, faces) {
  const THREE = window.THREE;
  const pos = [];
  for (const fc of faces) {
    for (let k = 1; k < fc.length - 1; k++) {
      for (const index of [fc[0], fc[k], fc[k + 1]]) {
        const v = verts[index];
        pos.push(v[0], v[2], -v[1]);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  return g;
}

function creasedNormals(g, angleDeg) {
  const THREE = window.THREE;
  const pos = g.attributes.position.array;
  const nTri = pos.length / 9;
  const fn = new Float32Array(nTri * 3);
  const un = new Float32Array(nTri * 3);
  for (let t = 0; t < nTri; t++) {
    const o = t * 9;
    const e1x = pos[o + 3] - pos[o];
    const e1y = pos[o + 4] - pos[o + 1];
    const e1z = pos[o + 5] - pos[o + 2];
    const e2x = pos[o + 6] - pos[o];
    const e2y = pos[o + 7] - pos[o + 1];
    const e2z = pos[o + 8] - pos[o + 2];
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    const l = Math.hypot(nx, ny, nz) || 1;
    fn[t * 3] = nx;
    fn[t * 3 + 1] = ny;
    fn[t * 3 + 2] = nz;
    un[t * 3] = nx / l;
    un[t * 3 + 1] = ny / l;
    un[t * 3 + 2] = nz / l;
  }
  /** @type {Map<string, number[]>} */
  const map = new Map();
  for (let v = 0; v < nTri * 3; v++) {
    const key = `${Math.round(pos[v * 3] * 500)},${Math.round(pos[v * 3 + 1] * 500)},${Math.round(pos[v * 3 + 2] * 500)}`;
    let arr = map.get(key);
    if (!arr) {
      arr = [];
      map.set(key, arr);
    }
    arr.push(v);
  }
  const cosT = Math.cos(rad(angleDeg));
  const nor = new Float32Array(pos.length);
  for (const list of map.values()) {
    for (const vi of list) {
      const ta = (vi / 3) | 0;
      let sx = 0;
      let sy = 0;
      let sz = 0;
      for (const vj of list) {
        const tb = (vj / 3) | 0;
        const dot = un[ta * 3] * un[tb * 3] + un[ta * 3 + 1] * un[tb * 3 + 1] + un[ta * 3 + 2] * un[tb * 3 + 2];
        if (dot >= cosT) {
          sx += fn[tb * 3];
          sy += fn[tb * 3 + 1];
          sz += fn[tb * 3 + 2];
        }
      }
      const ll = Math.hypot(sx, sy, sz) || 1;
      nor[vi * 3] = sx / ll;
      nor[vi * 3 + 1] = sy / ll;
      nor[vi * 3 + 2] = sz / ll;
    }
  }
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  return g;
}

function capInto(verts, faces, ring, want, plane) {
  const THREE = window.THREE;
  const pts = ring.map((i) =>
    plane === 'xy' ? new THREE.Vector2(verts[i][0], verts[i][1]) : new THREE.Vector2(verts[i][1], verts[i][2])
  );
  const tris = THREE.ShapeUtils.triangulateShape(pts, []);
  for (const tri of tris) {
    const a = ring[tri[0]];
    const b = ring[tri[1]];
    const c = ring[tri[2]];
    const A = verts[a];
    const B = verts[b];
    const C = verts[c];
    const ux = B[0] - A[0];
    const uy = B[1] - A[1];
    const uz = B[2] - A[2];
    const wx = C[0] - A[0];
    const wy = C[1] - A[1];
    const wz = C[2] - A[2];
    const nx = uy * wz - uz * wy;
    const ny = uz * wx - ux * wz;
    const nz = ux * wy - uy * wx;
    faces.push(nx * want[0] + ny * want[1] + nz * want[2] >= 0 ? [a, b, c] : [a, c, b]);
  }
}

function sweepGeom(profile, z0 = 0) {
  const prof = densify(profile);
  const verts = [];
  const rings = [];
  for (const [o, z] of prof) {
    const ring = [];
    for (const point of rrect(W / 2 + o, D / 2 + o, R + o)) {
      ring.push(verts.length);
      verts.push([point[0], point[1], z + z0]);
    }
    rings.push(ring);
  }
  const n = rings[0].length;
  const faces = [];
  for (let r = 0; r < rings.length - 1; r++) {
    const A = rings[r];
    const B = rings[r + 1];
    for (let k = 0; k < n; k++) {
      const k2 = (k + 1) % n;
      faces.push([A[k], A[k2], B[k2], B[k]]);
    }
  }
  capInto(verts, faces, rings[0], [0, 0, -1], 'xy');
  capInto(verts, faces, rings[rings.length - 1], [0, 0, 1], 'xy');
  return creasedNormals(buildGeom(verts, faces), 40);
}

function latheGeom(profile, n = 40, crease = 50) {
  const prof = densify(profile, 0.05, 0.3);
  const verts = [];
  const rings = [];
  for (const [radius, z] of prof) {
    const ring = [];
    for (let k = 0; k < n; k++) {
      const a = (2 * Math.PI * k) / n;
      ring.push(verts.length);
      verts.push([radius * Math.cos(a), radius * Math.sin(a), z]);
    }
    rings.push(ring);
  }
  const faces = [];
  for (let r = 0; r < rings.length - 1; r++) {
    const A = rings[r];
    const B = rings[r + 1];
    for (let q = 0; q < n; q++) {
      const q2 = (q + 1) % n;
      faces.push([A[q], A[q2], B[q2], B[q]]);
    }
  }
  capInto(verts, faces, rings[0], [0, 0, -1], 'xy');
  capInto(verts, faces, rings[rings.length - 1], [0, 0, 1], 'xy');
  return creasedNormals(buildGeom(verts, faces), crease);
}

function extrudeGeom(poly, x0, x1, bevel = 0.35, crease = 35) {
  poly = ensureCCW(dedupe(poly));
  const n = poly.length;
  const ip = insetAll(poly, bevel);
  const rings = [
    [ip, x0],
    [poly, x0 + bevel],
    [poly, x1 - bevel],
    [ip, x1],
  ];
  const verts = [];
  const idx = [];
  for (const [ring, x] of rings) {
    const r = [];
    for (let j = 0; j < n; j++) {
      r.push(verts.length);
      verts.push([x, ring[j][0], ring[j][1]]);
    }
    idx.push(r);
  }
  const faces = [];
  for (let s = 0; s < 3; s++) {
    const A = idx[s];
    const B = idx[s + 1];
    for (let k = 0; k < n; k++) {
      const k2 = (k + 1) % n;
      faces.push([A[k], A[k2], B[k2], B[k]]);
    }
  }
  capInto(verts, faces, idx[0], [-1, 0, 0], 'yz');
  capInto(verts, faces, idx[3], [1, 0, 0], 'yz');
  return creasedNormals(buildGeom(verts, faces), crease);
}

function rodGeom(r, len, seg = 24) {
  const g = new window.THREE.CylinderGeometry(r, r, len, seg);
  g.rotateZ(Math.PI / 2);
  return g;
}

function bodyProfile() {
  return [[-8, 0]].concat(
    arc(-1.5, 1.5, 1.5, -90, 0, 5),
    [
      [0, HB - 7],
      [0.8, HB - 6.2],
      [0.8, HB - 1.2],
    ],
    arc(-0.4, HB - 1.2, 1.2, 0, 90, 5),
    [[-1.9, HB]],
    arc(-1.9, HB - 0.6, 0.6, 90, 180, 4),
    [[-2.5, 6.6]],
    arc(-3.1, 6.6, 0.6, 0, -90, 4),
    [[-8, 6]]
  );
}

function lidProfile() {
  return [[-8, HL - WALL]].concat(
    arc(-3.1, HL - WALL - 0.6, 0.6, 90, 0, 4),
    [[-2.5, 0.6]],
    arc(-1.9, 0.6, 0.6, 180, 270, 4),
    [[0.2, 0]],
    arc(0.2, 0.6, 0.6, 270, 360, 4),
    [
      [0.8, 6.2],
      [0, 7],
      [0, HL - 5],
    ],
    arc(-5, HL - 5, 5, 0, 90, 12),
    [[-8, HL]]
  );
}

function padProfile() {
  const top = HL - WALL;
  const bot = top - 5;
  return [[-12, bot]].concat(arc(-4.5, bot + 1.5, 1.5, -90, 0, 6), [
    [-3, top],
    [-12, top],
  ]);
}

const CASE_PROFILE = [
  [1.5, 0.12],
  [2.8, 0.12],
  [2.85, 0],
  [6.6, 0],
  [6.76, 0.18],
  [6.76, 1.15],
  [6, 1.32],
  [6, 1.9],
  [6.51, 2.25],
  [6.51, 5.55],
  [6.35, 5.75],
  [6.24, 58.4],
  [4.3, 62.6],
  [4.3, 66.5],
  [3.95, 66.5],
  [3.95, 64],
];
const PRIMER_PROFILE = [
  [0.6, 0.03],
  [2.62, 0.03],
  [2.72, 0.1],
  [2.72, 0.8],
];

function bulletProfile() {
  const r0 = 3.91;
  const z0 = 69;
  const L = 15.6;
  const rho = (r0 * r0 + L * L) / (2 * r0);
  const p = [
    [3.2, 60],
    [3.91, 60.4],
    [3.91, z0],
  ];
  for (let i = 1; i < 15; i++) {
    const x = (L * i) / 15;
    p.push([Math.max(Math.sqrt(rho * rho - x * x) - rho + r0, 0.7), z0 + x]);
  }
  p.push([0.6, z0 + L + 0.15]);
  return p;
}

/**
 * Side profile of a clasp fin, as printed: flat against the seam with a rounded outer corner, a
 * straight outer edge past the pin hole, then a 45° chamfer back into the wall.
 * @param {1 | -1} direction - 1 for the lid (grows upwards from the seam), -1 for the base.
 */
function finProfile(direction) {
  const wall = Y0 + 1.5;
  const outer = Y0 - FIN_REACH;
  const seam = HB + direction * 0.8;
  const pin = direction > 0 ? ZP : ZB;
  const edgeEnd = pin + direction * 4;
  const chamferEnd = edgeEnd + direction * (wall - outer);
  const corner = arc(outer + 2, seam + direction * 2, 2, direction > 0 ? 270 : 90, 180, 8);
  return [[wall, seam], ...corner, [outer, edgeEnd], [wall, chamferEnd]];
}

function buildParts() {
  const claspCenter = densify(
    arc(YR, ZP, RC, 55, 180, 10).concat([[YR - RC, ZB]], arc(YR, ZB, RC, 180, 300, 10).slice(1)),
    0.3,
    2.0
  );
  return {
    body: sweepGeom(bodyProfile()),
    lid: sweepGeom(lidProfile(), HB),
    pad: sweepGeom(padProfile(), HB),
    tabBody: extrudeGeom(finProfile(-1), -3.5, 3.5),
    tabLid: extrudeGeom(finProfile(1), -3.5, 3.5),
    knBody: extrudeGeom([[D / 2 - 1.5, HB - 0.6]].concat(arc(YH, HB, 4.2, 125, -90, 20), [[D / 2 - 1.5, HB - 13]]), -7.3, 7.3),
    knLid: extrudeGeom([[D / 2 - 1.5, HB + 0.6]].concat(arc(YH, HB, 4.2, -125, 90, 20), [[D / 2 - 1.5, HB + 13]]), -8, 8),
    clasp: extrudeGeom(offsetPath(claspCenter, 1.5), -15.5, 15.5, 0.4, 40),
    rod: rodGeom(PIN_R, 46.8),
    pin: rodGeom(1.3, 47, 16),
    rib: rodGeom(0.75, 27, 12),
  };
}

function easeIO(k) {
  return k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
}

function easeOut(k) {
  return 1 - Math.pow(1 - k, 3);
}

function lin(k) {
  return k;
}

/** Backdrop of the order picture, close to the dark stage behind the preview. */
const PICTURE_BACKDROP = '#15171c';
/** Size of the order picture, in pixels. */
const PICTURE_SIZE = [1800, 1000];
/**
 * Views in the order picture, as [azimuth, elevation, distance, x, y, width, height]: the box from
 * three quarters on the left, and its lid and front, where the engravings go, on the right.
 */
const PICTURE_PANELS = [
  [-32, 24, 400, 0, 0, 1000, 1000],
  [0, 86, 330, 1004, 0, 796, 498],
  [0, 8, 380, 1004, 502, 796, 498],
];

/**
 * Puts a picture in a file field of the product form, so it is uploaded with the order.
 * @param {HTMLCanvasElement} picture
 * @param {HTMLInputElement} input
 */
function attachPicture(picture, input) {
  const [, data = ''] = picture.toDataURL('image/jpeg', 0.85).split(',');
  const bytes = Uint8Array.from(atob(data), (character) => character.charCodeAt(0));
  try {
    const transfer = new DataTransfer();
    transfer.items.add(new File([bytes], 'diseno-3d.jpg', { type: 'image/jpeg' }));
    input.files = transfer.files;
  } catch {
    // Browsers that cannot set files send the order without the picture
  }
}

const VIEWS = {
  v34: [-32, 24, 640],
  front: [0, 8, 600],
  back: [150, 20, 620],
  top: [-12, 58, 560],
};

class BoxScene {
  lidOpen = false;
  lidBusy = false;
  spinning = false;
  /** @type {string | null} */
  currentView = 'v34';

  #needsRender = true;
  #frame = 0;
  #visible = true;
  /** @type {Array<{ start: number, dur: number, fn: (k: number) => void, ease: (k: number) => number, done?: () => void }>} */
  #tweens = [];
  /** @type {Array<() => void>} */
  #queue = [];
  #flying = 0;
  #lid = { angle: 0, clasp: 0 };
  #speed = 1;
  /** @type {Engraving[]} */
  #engravings = [];
  #fontFamily = 'sans-serif';
  /** Flag images by style: '' for the brush-stroke flag, 'classic' for the rectangular one. */
  #flagImages = /** @type {Record<string, HTMLImageElement>} */ ({});
  /** Images of the other flags, by URL. */
  #flagArt = /** @type {Map<string, HTMLImageElement>} */ (new Map());
  /** Other flags cut out with the brush-stroke silhouette, by URL. */
  #brushFlags = /** @type {Map<string, HTMLCanvasElement>} */ (new Map());
  /** @type {HTMLCanvasElement | null} */
  #brushMask = null;
  /** Drawings for the listed animals, by `animalKey` of the animal name. */
  #animalArt = /** @type {Record<string, string>} */ ({});
  /** One-colour masks of animal drawings, by URL: a canvas once ready, or 'loading' / 'error'. */
  #masks = /** @type {Map<string, HTMLCanvasElement | string>} */ (new Map());
  /** Last mask filled with an engraving colour. */
  #tinted = { key: '', canvas: /** @type {HTMLCanvasElement | null} */ (null) };
  /** CSS font-family lists for engraved texts, keyed by `Engraving.font`. */
  #engravingFonts = /** @type {Record<string, string>} */ ({});
  /** @type {() => void} */
  #onStateChange;
  #abortController = new AbortController();

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {HTMLElement} viewport
   * @param {{ fontFamily: string, flagSrc: string, classicFlagSrc?: string, animalArt?: Record<string, string>, engravingFonts: Record<string, { family: string, src: string }>, onStateChange: () => void }} options
   */
  constructor(canvas, viewport, options) {
    const THREE = window.THREE;
    this.canvas = canvas;
    this.viewport = viewport;
    this.#fontFamily = options.fontFamily;
    for (const [name, src] of Object.entries(options.animalArt ?? {})) this.#animalArt[animalKey(name)] = src;
    for (const [name, { family, src }] of Object.entries(options.engravingFonts)) {
      this.#engravingFonts[name] = `"${family}", ${options.fontFamily}`;
      if (src) this.#loadFont(family, src);
    }
    for (const [style, src] of [
      ['', options.flagSrc],
      ['classic', options.classicFlagSrc],
    ]) {
      if (!src) continue;
      const flag = new Image();
      flag.crossOrigin = 'anonymous';
      flag.addEventListener('load', () => this.redrawEngravings(), { signal: this.#abortController.signal });
      flag.src = src;
      this.#flagImages[style] = flag;
    }
    this.#onStateChange = options.onStateChange;
    this.#speed = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0.25 : 1;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    THREE.ColorManagement.legacyMode = false;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    this.renderer = renderer;

    this.scene = new THREE.Scene();
    this.scene.environment = this.#studioEnv();

    this.materials = {
      pla: new THREE.MeshPhysicalMaterial({ color: DEFAULT_COLORS.box, roughness: 0.5, metalness: 0, clearcoat: 0.12, clearcoatRoughness: 0.6, envMapIntensity: 0.6 }),
      clasp: new THREE.MeshPhysicalMaterial({ color: DEFAULT_COLORS.clasp, roughness: 0.5, metalness: 0, clearcoat: 0.12, clearcoatRoughness: 0.6, envMapIntensity: 0.6 }),
      foam: new THREE.MeshStandardMaterial({ color: '#EEECE6', roughness: 0.9, metalness: 0, envMapIntensity: 0.5 }),
      hole: new THREE.MeshStandardMaterial({ color: '#B9B3A8', roughness: 0.95, metalness: 0, side: THREE.BackSide }),
      brass: new THREE.MeshStandardMaterial({ color: '#F2D193', roughness: 0.17, metalness: 1, envMapIntensity: 1.15 }),
      nickel: new THREE.MeshStandardMaterial({ color: '#CFC9BE', roughness: 0.34, metalness: 1, envMapIntensity: 1 }),
      copper: new THREE.MeshStandardMaterial({ color: '#F0B497', roughness: 0.22, metalness: 1, envMapIntensity: 1.1 }),
      proxy: new THREE.MeshBasicMaterial({ visible: false }),
      lockBody: new THREE.MeshStandardMaterial({ color: '#17181B', roughness: 0.55, metalness: 0.2, envMapIntensity: 0.6 }),
      lockHole: new THREE.MeshBasicMaterial({ color: '#0B0C0E' }),
    };
    this.cartridgeParts = {
      caseG: latheGeom(CASE_PROFILE, 40),
      bullet: latheGeom(bulletProfile(), 40),
      primer: latheGeom(PRIMER_PROFILE, 24),
      proxy: new THREE.CylinderGeometry(8.6, 8.6, 92, 12),
      hole: new THREE.CylinderGeometry(7.15, 7.15, 30, 32, 1, true),
    };

    this.#addLights();
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(3000, 3000), new THREE.ShadowMaterial({ opacity: 0.4 }));
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = -14.05;
    this.ground.receiveShadow = true;
    this.scene.add(this.ground);
    this.woodTexture = this.#woodTexture();

    this.camera = new THREE.PerspectiveCamera(28, 1.5, 5, 8000);
    const controls = new THREE.OrbitControls(this.camera, canvas);
    controls.target.set(0, 48, 0);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = 260;
    controls.maxDistance = 1600;
    controls.maxPolarAngle = rad(87);
    controls.rotateSpeed = 0.8;
    controls.autoRotateSpeed = 2.2;
    controls.addEventListener('start', () => {
      this.currentView = null;
      this.#setSpin(false);
      this.#onStateChange();
    });
    controls.addEventListener('change', () => {
      this.#needsRender = true;
      this.#requestFrame();
    });
    this.controls = controls;

    this.#bindPointer();
    this.resizeObserver = new ResizeObserver(() => this.#resize());
    this.resizeObserver.observe(viewport);
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#visible = entry?.isIntersecting ?? true;
      if (this.#visible) this.#requestFrame();
    });
    this.intersectionObserver.observe(viewport);
    this.#resize();
    this.#requestFrame();
  }

  /**
   * Registers an engraving font once per page and redraws when it is ready.
   * @param {string} family
   * @param {string} src
   */
  async #loadFont(family, src) {
    try {
      let font = [...document.fonts].find((face) => face.family.replace(/"/g, '') === family);
      if (!font) {
        font = new FontFace(family, `url("${src}")`);
        document.fonts.add(font);
      }
      await font.load();
    } catch {
      // The theme font stays as the fallback
      return;
    }
    if (!this.#abortController.signal.aborted) this.redrawEngravings();
  }

  /**
   * Rebuilds the box for a capacity, number of clasps and padlock option.
   * @param {BoxLayout} layout
   */
  setLayout({ capacity, clasps, lockLugs, padlock }) {
    const [cols, rows] = gridFor(capacity);
    applyGrid(cols, rows);
    this.#tweens = [];
    this.#queue = [];
    this.#flying = 0;
    this.lidBusy = false;
    this.lidOpen = false;
    this.#lid = { angle: 0, clasp: 0 };
    this.#disposeBox();
    this.#buildBox(capacity, clasps);
    this.#buildLock(lockLugs, padlock);
    this.#applyLid();
    this.#setSph(rad(VIEWS.v34[0]), rad(VIEWS.v34[1]), this.#fitD(VIEWS.v34[2]));
    this.currentView = 'v34';
    this.#onStateChange();
  }

  /**
   * @param {{ box: string, clasp: string }} colors
   */
  setColors({ box, clasp }) {
    this.materials.pla.color.set(box);
    this.materials.clasp.color.set(clasp);
    this.#needsRender = true;
    this.#requestFrame();
  }

  /**
   * @param {Engraving[]} engravings
   */
  setEngravings(engravings) {
    this.#engravings = engravings;
    this.redrawEngravings();
  }

  redrawEngravings() {
    if (!this.box) return;
    this.#drawFace('lid');
    this.#drawFace('front');
  }

  get cartridgesOut() {
    return (this.cartridges ?? []).some((c) => c.out);
  }

  toggleLid() {
    this.#stopSpin();
    this.#setLid(!this.lidOpen);
  }

  toggleCartridges() {
    this.#stopSpin();
    const out = !this.cartridgesOut;
    this.#whenOpen(() => {
      let n = 0;
      for (const c of this.cartridges) {
        if (c.out !== out && !c.busy) this.#fly(c, out ? 1 : 0, n++ * 70 * this.#speed);
      }
    });
  }

  toggleSpin() {
    this.#setSpin(!this.spinning);
    if (this.spinning) this.currentView = null;
    this.#onStateChange();
  }

  /**
   * @param {string} name
   */
  goView(name) {
    const view = VIEWS[name];
    if (!view) return;
    if (name === 'top' && this.lidOpen) this.#setLid(false);
    this.#stopSpin();
    this.currentView = name;
    this.#onStateChange();
    const cur = this.#getSph();
    let daz = rad(view[0]) - cur.az;
    daz = Math.atan2(Math.sin(daz), Math.cos(daz));
    const to = { az: cur.az + daz, el: rad(view[1]), d: this.#fitD(view[2]) };
    this.#tween(900 * this.#speed, (k) => {
      this.#setSph(cur.az + (to.az - cur.az) * k, cur.el + (to.el - cur.el) * k, cur.d + (to.d - cur.d) * k);
    });
  }

  /**
   * The closed box from three quarters, from above and from the front, as one picture for the order.
   * The glossy finishes would mirror the studio lights over the lid, so the picture is drawn matte and
   * a little darker. The customer's view, size and finishes are restored before the next frame.
   * @returns {HTMLCanvasElement | null}
   */
  snapshot() {
    const THREE = window.THREE;
    const { renderer, camera } = this;
    const picture = document.createElement('canvas');
    [picture.width, picture.height] = PICTURE_SIZE;
    const g = picture.getContext('2d');
    if (!g) return null;
    g.fillStyle = PICTURE_BACKDROP;
    g.fillRect(0, 0, picture.width, picture.height);

    const before = { sph: this.#getSph(), lid: this.#lid, size: renderer.getSize(new THREE.Vector2()), ratio: renderer.getPixelRatio(), exposure: renderer.toneMappingExposure };
    const wood = Array.isArray(this.turntable?.material) ? this.turntable.material[1] : null;
    const finishes = [this.materials.pla, this.materials.clasp, wood].filter(Boolean).map((material) => {
      const finish = { material, roughness: material.roughness, clearcoat: material.clearcoat, envMapIntensity: material.envMapIntensity };
      material.roughness = Math.max(material.roughness, 0.85);
      // Kept above zero: a clearcoat switched off and on again would rebuild the shader twice
      material.clearcoat *= 0.05;
      material.envMapIntensity *= 0.5;
      return finish;
    });
    renderer.toneMappingExposure = 0.92;
    this.#lid = { angle: 0, clasp: 0 };
    this.#applyLid();
    renderer.setPixelRatio(1);
    try {
      for (const [az, el, d, x, y, width, height] of PICTURE_PANELS) {
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        this.#setSph(rad(az), rad(el), this.#fitD(d));
        renderer.render(this.scene, camera);
        g.drawImage(renderer.domElement, x, y, width, height);
      }
    } finally {
      for (const { material, ...finish } of finishes) Object.assign(material, finish);
      renderer.toneMappingExposure = before.exposure;
      this.#lid = before.lid;
      this.#applyLid();
      renderer.setPixelRatio(before.ratio);
      renderer.setSize(before.size.x, before.size.y, false);
      camera.aspect = before.size.x / before.size.y;
      camera.updateProjectionMatrix();
      this.#setSph(before.sph.az, before.sph.el, before.sph.d);
    }
    return picture;
  }

  dispose() {
    cancelAnimationFrame(this.#frame);
    this.#abortController.abort();
    this.resizeObserver.disconnect();
    this.intersectionObserver.disconnect();
    this.controls.dispose();
    this.#disposeBox();
    for (const geometry of Object.values(this.cartridgeParts)) geometry.dispose();
    for (const material of Object.values(this.materials)) material.dispose();
    this.woodTexture.dispose();
    this.scene.environment?.dispose();
    this.ground.geometry.dispose();
    this.ground.material.dispose();
    this.renderer.dispose();
  }

  #buildBox(capacity, clasps) {
    const THREE = window.THREE;
    const M = this.materials;
    const parts = buildParts();
    this.parts = parts;
    /** @type {Array<{ dispose: () => void }>} */
    this.disposables = Object.values(parts);

    const add = (parent, geo, mat, pos, cast = true) => {
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = cast;
      mesh.receiveShadow = true;
      if (pos) mesh.position.set(pos[0], pos[1], pos[2]);
      parent.add(mesh);
      return mesh;
    };
    const own = (geometry) => {
      this.disposables.push(geometry);
      return geometry;
    };

    const box = new THREE.Group();
    this.box = box;
    this.scene.add(box);
    const bodyMesh = add(box, parts.body, M.pla);

    const shape = new THREE.Shape();
    const outline = rrect(W / 2 - 2.65, D / 2 - 2.65, R - 2.65);
    shape.moveTo(outline[0][0], outline[0][1]);
    for (const point of outline.slice(1)) shape.lineTo(point[0], point[1]);
    shape.closePath();
    for (const x of XS) {
      for (const y of YS) {
        const hole = new THREE.Path();
        hole.absarc(x, y, 7.15, 0, Math.PI * 2, true);
        shape.holes.push(hole);
      }
    }
    const insertGeometry = own(new THREE.ShapeGeometry(shape, 24));
    insertGeometry.translate(0, 0, INS_TOP);
    insertGeometry.rotateX(-Math.PI / 2);
    const insertMesh = add(box, insertGeometry, M.foam, null, false);
    for (const x of XS) {
      for (const y of YS) add(box, this.cartridgeParts.hole, M.hole, [x, INS_TOP - 15, -y], false);
    }

    const latchX = clasps === 1 ? [0] : [-W / 4, W / 4];
    // The small box is printed with a single hinge in the middle of its back
    const hingeX = capacity <= SINGLE_CLASP_MAX_CAPACITY ? [0] : [-W / 4, W / 4];
    for (const c of latchX) {
      add(box, parts.tabBody, M.pla, [c - 19.5, 0, 0]);
      add(box, parts.tabBody, M.pla, [c + 19.5, 0, 0]);
      add(box, parts.rod, M.pla, [c, ZB, -YR]);
    }
    for (const c of hingeX) {
      add(box, parts.knBody, M.pla, [c - 15.7, 0, 0]);
      add(box, parts.knBody, M.pla, [c + 15.7, 0, 0]);
      add(box, parts.pin, M.pla, [c, HB, -YH]);
    }

    const faceWidth = W - 20;
    this.faces = {
      front: this.#createFace(faceWidth, FRONT_HEIGHT),
      lid: this.#createFace(faceWidth, D - 12),
    };
    const frontDecal = add(box, own(new THREE.PlaneGeometry(faceWidth, FRONT_HEIGHT)), this.faces.front.material, null, false);
    frontDecal.position.set(0, FRONT_CENTER, D / 2 + 0.06);

    const lidPivot = new THREE.Group();
    lidPivot.position.set(0, HB, -YH);
    box.add(lidPivot);
    const lidInner = new THREE.Group();
    lidInner.position.set(0, -HB, YH);
    lidPivot.add(lidInner);
    const lidMesh = add(lidInner, parts.lid, M.pla);
    add(lidInner, parts.pad, M.foam);
    for (const c of hingeX) add(lidInner, parts.knLid, M.pla, [c, 0, 0]);
    this.claspPivots = [];
    for (const c of latchX) {
      add(lidInner, parts.tabLid, M.pla, [c - 19.5, 0, 0]);
      add(lidInner, parts.tabLid, M.pla, [c + 19.5, 0, 0]);
      add(lidInner, parts.rod, M.pla, [c, ZP, -YR]);
      const pivot = new THREE.Group();
      pivot.position.set(c, ZP, -YR);
      lidInner.add(pivot);
      const inner = new THREE.Group();
      inner.position.set(0, -ZP, YR);
      pivot.add(inner);
      add(inner, parts.clasp, M.clasp);
      for (let k = 0; k < 3; k++) add(inner, parts.rib, M.clasp, [0, ZB + 4.5 + k * 3, -(YR - RC - 1.35)]);
      this.claspPivots.push(pivot);
    }
    const lidDecal = add(lidInner, own(new THREE.PlaneGeometry(faceWidth, D - 12)), this.faces.lid.material, null, false);
    lidDecal.rotation.x = -Math.PI / 2;
    lidDecal.position.set(0, HB + HL + 0.06, 0);
    this.lidPivot = lidPivot;
    this.lidInner = lidInner;

    // Cartridges sit in the insert and fly out to rows in front of the box
    this.cartridges = [];
    this.proxies = [];
    let placed = 0;
    YS.forEach((yb, row) => {
      XS.forEach((x, col) => {
        if (placed++ >= capacity) return;
        const group = new THREE.Group();
        const inner = new THREE.Group();
        inner.position.y = -CART_HALF;
        group.add(inner);
        add(inner, this.cartridgeParts.caseG, M.brass);
        add(inner, this.cartridgeParts.bullet, M.copper);
        add(inner, this.cartridgeParts.primer, M.nickel, null, false);
        const proxy = new THREE.Mesh(this.cartridgeParts.proxy, M.proxy);
        group.add(proxy);
        this.proxies.push(proxy);
        const cartridge = {
          g: group,
          t: 0,
          out: false,
          busy: false,
          inP: new THREE.Vector3(x, INS_TOP + HEAD_UP - CART_HALF, -yb),
          outP: new THREE.Vector3((col - (XS.length - 1) / 2) * 17.6, CART_HALF, D / 2 + 31 + (YS.length - 1 - row) * 20),
        };
        proxy.userData.cart = cartridge;
        this.cartridges.push(cartridge);
        box.add(group);
        this.#poseCart(cartridge);
      });
    });
    this.occluders = [bodyMesh, lidMesh, insertMesh];

    // Wooden turntable sized to the box and the cartridges laid out in front
    const reach = Math.hypot(W / 2, D / 2 + 31 + (YS.length - 1) * 20) + 40;
    const radius = Math.max(232, reach);
    const turntableGeometry = own(new THREE.CylinderGeometry(radius, radius + 4, 14, 128));
    const turntableMaterials = [
      new THREE.MeshPhysicalMaterial({ color: '#2A1A10', roughness: 0.6, metalness: 0 }),
      new THREE.MeshPhysicalMaterial({ map: this.woodTexture, roughness: 0.5, metalness: 0, clearcoat: 0.35, clearcoatRoughness: 0.28, envMapIntensity: 0.5 }),
      new THREE.MeshStandardMaterial({ color: '#120C08', roughness: 1 }),
    ];
    this.disposables.push(...turntableMaterials);
    const turntable = new THREE.Mesh(turntableGeometry, turntableMaterials);
    turntable.position.y = -7.02;
    turntable.receiveShadow = true;
    turntable.castShadow = true;
    this.scene.add(turntable);
    this.turntable = turntable;

    this.redrawEngravings();
    this.#needsRender = true;
    this.#requestFrame();
  }

  /**
   * Adds the padlock lugs between the clasps and, when included, a combination padlock: hanging from
   * the lugs or, without them, lying on the turntable next to the box.
   * @param {boolean} lockLugs
   * @param {boolean} padlock
   */
  #buildLock(lockLugs, padlock) {
    const THREE = window.THREE;
    const M = this.materials;
    this.padlock = null;
    this.padlockHanging = false;
    if (!this.box || !this.lidInner || (!lockLugs && !padlock)) return;

    const own = (geometry) => {
      this.disposables.push(geometry);
      return geometry;
    };
    const add = (parent, geometry, material, position) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.position.set(position[0], position[1], position[2]);
      parent.add(mesh);
      return mesh;
    };

    // Frames sit between the inner faces of the inner clasp fins (fins are 7 mm wide, 19.5 mm from each clasp)
    const holeZ = D / 2 + LOCK_FRAME_DEPTH / 2;
    if (lockLugs) {
      const span = 2 * (W / 4 - 19.5 - 3.5);
      const frameZ = D / 2 + LOCK_FRAME_DEPTH / 2;
      const shelf = own(new THREE.BoxGeometry(span, LOCK_SHELF, LOCK_FRAME_DEPTH));
      const hole = own(new THREE.CircleGeometry(2.2, 20));
      // [parent, direction away from the seam]
      const shelves = [
        [this.lidInner, 1],
        [this.box, -1],
      ];
      for (const [parent, direction] of shelves) {
        const seam = HB + direction * 0.8;
        add(parent, shelf, M.pla, [0, seam + (direction * LOCK_SHELF) / 2, frameZ]);
        // The hole shows on the outer face of each shelf: on top of the lid one and under the base one
        const disc = add(parent, hole, M.lockHole, [0, seam + direction * (LOCK_SHELF + 0.05), holeZ]);
        disc.rotation.x = -direction * (Math.PI / 2);
        disc.castShadow = false;
      }
    }
    if (!padlock) return;

    // Padlock built around the body center: shackle legs at x = ±6 above it, number dials on the +x face
    const group = new THREE.Group();
    add(group, own(new THREE.BoxGeometry(22, 26, 11)), M.lockBody, [3, 0, 0]);
    const leg = own(new THREE.CylinderGeometry(1.6, 1.6, 22, 16));
    add(group, leg, M.nickel, [-6, 24, 0]);
    add(group, leg, M.nickel, [6, 24, 0]);
    add(group, own(new THREE.TorusGeometry(6, 1.6, 12, 24, Math.PI)), M.nickel, [0, 35, 0]);
    const dial = own(new THREE.CylinderGeometry(3.4, 3.4, 9, 20));
    for (const y of [-7, 0, 7]) {
      const mesh = add(group, dial, M.nickel, [14, y, 0]);
      mesh.rotation.x = Math.PI / 2;
    }

    if (lockLugs) {
      // Turned 60° away from the box: the back leg goes down through both shelves, the front leg and
      // the body hang in front of the base frame with the dials towards the customer
      const angle = -Math.PI / 3;
      group.rotation.y = angle;
      // Place the back leg (local x = -6) on the hole
      group.position.set(6 * Math.cos(angle), HB - 0.8 - LOCK_SHELF - 13 - 2, holeZ - 6 * Math.sin(angle));
      this.padlockHanging = true;
    } else {
      // Not fitted to the box: lies face up on the turntable beside it
      group.rotation.x = -Math.PI / 2;
      group.position.set(W / 2 + 30, 5.5, D / 2 + 12);
    }
    this.box.add(group);
    this.padlock = group;
  }

  #disposeBox() {
    if (this.box) this.scene.remove(this.box);
    if (this.turntable) this.scene.remove(this.turntable);
    for (const item of this.disposables ?? []) item.dispose();
    for (const face of Object.values(this.faces ?? {})) {
      face.texture.dispose();
      face.material.dispose();
    }
    this.box = null;
    this.turntable = null;
    this.disposables = [];
    this.faces = null;
    this.cartridges = [];
    this.proxies = [];
  }

  /**
   * The picture of a flag engraving. Spain keeps the shop's brush-stroke and classic images; any other
   * flag is its own image, cut out with the brush-stroke silhouette unless the classic style is chosen.
   * Images load once and the faces are redrawn when they arrive.
   * @param {Engraving} item
   * @returns {HTMLImageElement | HTMLCanvasElement | null}
   */
  #flagImage(item) {
    if (!item.art || item.code === SPAIN_FLAG_CODE) {
      const image = this.#flagImages[item.value];
      return image?.naturalWidth ? image : null;
    }
    let image = this.#flagArt.get(item.art);
    if (!image) {
      image = new Image();
      image.crossOrigin = 'anonymous';
      image.addEventListener('load', () => this.redrawEngravings(), { signal: this.#abortController.signal });
      image.src = item.art;
      this.#flagArt.set(item.art, image);
    }
    if (!image.complete || !image.naturalWidth) return null;
    if (item.value === 'classic') return image;

    const cached = this.#brushFlags.get(item.art);
    if (cached) return cached;
    const mask = this.#brushSilhouette();
    // Until the brush-stroke image has loaded, the plain flag stands in
    if (!mask) return image;
    const canvas = document.createElement('canvas');
    canvas.width = mask.width;
    canvas.height = mask.height;
    const g = canvas.getContext('2d');
    if (!g) return image;
    // Cover the silhouette with the flag, then keep only the silhouette
    const scale = Math.max(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    g.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(mask, 0, 0);
    this.#brushFlags.set(item.art, canvas);
    return canvas;
  }

  /**
   * The brush-stroke silhouette of the Spanish flag image with its three strokes merged into one
   * patch, so flags with vertical stripes or a canton keep their design.
   * @returns {HTMLCanvasElement | null}
   */
  #brushSilhouette() {
    if (this.#brushMask) return this.#brushMask;
    const brush = this.#flagImages[''];
    if (!brush?.naturalWidth) return null;
    // Stacking copies a little lower each time closes the gaps between the strokes
    const reach = Math.round(brush.naturalHeight * 0.18);
    const canvas = document.createElement('canvas');
    canvas.width = brush.naturalWidth;
    canvas.height = brush.naturalHeight + reach;
    const g = canvas.getContext('2d');
    if (!g) return null;
    for (let y = 0; y <= reach; y += 3) g.drawImage(brush, 0, y);
    this.#brushMask = canvas;
    return canvas;
  }

  /**
   * @param {number} widthMm
   * @param {number} heightMm
   */
  #createFace(widthMm, heightMm) {
    const THREE = window.THREE;
    const canvas = document.createElement('canvas');
    canvas.width = 2048;
    canvas.height = Math.max(64, Math.round((2048 * heightMm) / widthMm));
    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.anisotropy = 8;
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      transparent: true,
      roughness: 0.55,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      depthWrite: false,
      envMapIntensity: 0.5,
    });
    return { canvas, texture, material, widthMm, heightMm };
  }

  /**
   * Draws every engraving placed on a face. Columns split the face into thirds and side columns
   * split again into a top and a bottom half.
   * @param {'lid' | 'front'} faceName
   */
  #drawFace(faceName) {
    const face = this.faces?.[faceName];
    if (!face) return;
    const { canvas, widthMm, heightMm } = face;
    const g = canvas.getContext('2d');
    if (!g) return;
    const s = canvas.width / widthMm;
    const slotWidth = widthMm / 3;
    g.clearRect(0, 0, canvas.width, canvas.height);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineCap = 'round';
    g.lineJoin = 'round';

    for (const item of this.#engravings) {
      if (item.face !== faceName) continue;
      const slotHeight = item.row === 0 ? heightMm : heightMm / 2;
      const cx = (widthMm / 2 + item.col * slotWidth) * s;
      const cy = (heightMm / 2 + (item.row * heightMm) / 4) * s;
      const maxWidth = (slotWidth - 4) * s;
      g.globalAlpha = item.sample ? 0.35 : 1;
      g.fillStyle = item.color;
      g.strokeStyle = item.color;

      if (item.kind === 'text') {
        // Real letter height, reduced only when the text would not fit its spot, so nothing is cut off
        const family = this.#engravingFonts[item.font] ?? this.#fontFamily;
        const fontSize = Math.min(item.size, slotHeight - 2) / CAP_HEIGHT;
        g.font = `400 ${fontSize * s}px ${family}`;
        const width = g.measureText(item.value).width;
        if (width > maxWidth) g.font = `400 ${(fontSize * s * maxWidth) / width}px ${family}`;
        g.fillText(item.value, cx, cy);
        continue;
      }

      if (item.kind === 'flag') {
        const image = this.#flagImage(item);
        if (!image) continue;
        // Chosen width, reduced only when it would not fit its spot
        const aspect =
          image instanceof HTMLImageElement ? image.naturalWidth / image.naturalHeight : image.width / image.height;
        const width = Math.min(item.size || DEFAULT_FLAG_MM, slotWidth - 2, (slotHeight - 2) * aspect) * s;
        const height = width / aspect;
        g.drawImage(image, cx - width / 2, cy - height / 2, width, height);
        continue;
      }

      const art = item.kind === 'animal' ? (item.art ?? this.#animalArt[animalKey(item.value)]) : '';
      if (art) {
        // Animal drawings are printed in one colour: draw them as a silhouette of that colour
        const silhouette = this.#silhouette(art, item.color);
        if (silhouette) {
          // Chosen size is the longest side; without one, or when it would not fit, it fills its spot
          const fit = Math.min(((slotWidth - 2) * s) / silhouette.width, (slotHeight * 0.95 * s) / silhouette.height);
          const chosen = item.size ? (item.size * s) / Math.max(silhouette.width, silhouette.height) : Infinity;
          const scale = Math.min(fit, chosen);
          const width = silhouette.width * scale;
          const height = silhouette.height * scale;
          g.drawImage(silhouette, cx - width / 2, cy - height / 2, width, height);
          continue;
        }
      }

      // No artwork: show the animal name, or the logo marker at its chosen width, inside a frame
      const label = item.value.toUpperCase();
      const frameWidth = item.size ? item.size * s : null;
      const fontSize = this.#fillFittedText(
        g,
        label,
        cx,
        cy,
        Math.min(4.5, slotHeight * 0.25) * s,
        (frameWidth ?? maxWidth) - 3 * s
      );
      const boxWidth = frameWidth ?? Math.min(g.measureText(label).width + 3 * s, maxWidth);
      const boxHeight = frameWidth ? Math.min(frameWidth * 0.6, (slotHeight - 2) * s) : fontSize * 1.9;
      g.lineWidth = 0.5 * s;
      g.strokeRect(cx - boxWidth / 2, cy - boxHeight / 2, boxWidth, boxHeight);
    }

    g.globalAlpha = 1;
    face.texture.needsUpdate = true;
    this.#needsRender = true;
    this.#requestFrame();
  }

  /**
   * An uploaded drawing filled with one colour, or null while it loads or if it cannot be read.
   * @param {string} url
   * @param {string} color
   * @returns {HTMLCanvasElement | null}
   */
  #silhouette(url, color) {
    const mask = this.#masks.get(url);
    if (mask === undefined) {
      this.#masks.set(url, 'loading');
      this.#loadMask(url);
      return null;
    }
    if (!(mask instanceof HTMLCanvasElement)) return null;

    const key = `${url}|${color}`;
    if (this.#tinted.key !== key) {
      const canvas = document.createElement('canvas');
      canvas.width = mask.width;
      canvas.height = mask.height;
      const g = canvas.getContext('2d');
      if (!g) return null;
      g.drawImage(mask, 0, 0);
      g.globalCompositeOperation = 'source-in';
      g.fillStyle = color;
      g.fillRect(0, 0, canvas.width, canvas.height);
      this.#tinted = { key, canvas };
    }
    return this.#tinted.canvas;
  }

  /**
   * Turns an animal image into a one-colour mask cropped to the drawing. Images with transparency
   * keep their shape; opaque ones keep the parts that contrast with the background at their edges.
   * @param {string} url
   */
  async #loadMask(url) {
    try {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await image.decode();
      const scale = Math.min(1, 512 / Math.max(image.naturalWidth, image.naturalHeight));
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      const source = document.createElement('canvas');
      source.width = width;
      source.height = height;
      const g = source.getContext('2d', { willReadFrequently: true });
      if (!g) throw new Error('No 2D context');
      g.drawImage(image, 0, 0, width, height);
      const pixels = g.getImageData(0, 0, width, height);
      const data = pixels.data;

      let transparent = 0;
      for (let i = 3; i < data.length; i += 4) if (data[i] < 200) transparent++;
      const useAlpha = transparent > (data.length / 4) * 0.02;

      // The edges are background: a dark edge means a light drawing on a dark background
      const luminanceAt = (/** @type {number} */ i) => 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      let edge = 0;
      for (let x = 0; x < width; x++) edge += luminanceAt(x * 4) + luminanceAt(((height - 1) * width + x) * 4);
      for (let y = 0; y < height; y++) edge += luminanceAt(y * width * 4) + luminanceAt((y * width + width - 1) * 4);
      const lightDrawing = edge / (2 * (width + height)) < 128;

      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      for (let i = 0; i < data.length; i += 4) {
        const ink = lightDrawing ? luminanceAt(i) : 255 - luminanceAt(i);
        const alpha = useAlpha ? data[i + 3] : Math.max(0, Math.min(255, (ink - 55) * 2.55)) * (data[i + 3] / 255);
        data[i] = data[i + 1] = data[i + 2] = 0;
        data[i + 3] = alpha;
        if (alpha > 40) {
          const x = (i / 4) % width;
          const y = Math.floor(i / 4 / width);
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
      if (maxX < 0) throw new Error('Empty drawing');
      g.putImageData(pixels, 0, 0);

      const mask = document.createElement('canvas');
      mask.width = maxX - minX + 1;
      mask.height = maxY - minY + 1;
      mask.getContext('2d')?.drawImage(source, minX, minY, mask.width, mask.height, 0, 0, mask.width, mask.height);
      this.#masks.set(url, mask);
    } catch {
      this.#masks.set(url, 'error');
    }
    if (!this.#abortController.signal.aborted) this.redrawEngravings();
  }

  /**
   * @returns {number} The font size used.
   */
  #fillFittedText(g, text, x, y, fontSize, maxWidth) {
    g.font = `700 ${fontSize}px ${this.#fontFamily}`;
    const width = g.measureText(text).width;
    if (width > maxWidth) {
      fontSize *= maxWidth / width;
      g.font = `700 ${fontSize}px ${this.#fontFamily}`;
    }
    g.fillText(text, x, y);
    return fontSize;
  }

  #woodTexture() {
    const THREE = window.THREE;
    const c = document.createElement('canvas');
    c.width = c.height = 1024;
    const g = c.getContext('2d');
    let seed = 7;
    const rnd = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    if (g) {
      for (let y = 0; y < 1024; y++) {
        const v = 0.5 + 0.25 * Math.sin(y * 0.045 + Math.sin(y * 0.011) * 3) + 0.15 * Math.sin(y * 0.31 + 1.7) + (rnd() - 0.5) * 0.16;
        g.fillStyle = `hsl(25,40%,${(12 + v * 13).toFixed(1)}%)`;
        g.fillRect(0, y, 1024, 1);
      }
      g.globalAlpha = 0.35;
      for (let i = 0; i < 80; i++) {
        const y0 = rnd() * 1024;
        g.strokeStyle = `hsl(24,45%,${(6 + rnd() * 7).toFixed(1)}%)`;
        g.lineWidth = 0.6 + rnd() * 1.8;
        g.beginPath();
        for (let x = 0; x <= 1024; x += 16) {
          const yy = y0 + Math.sin(x * 0.006 + i) * 6 + Math.sin(x * 0.02 + i * 3) * 1.5;
          if (x) g.lineTo(x, yy);
          else g.moveTo(x, yy);
        }
        g.stroke();
      }
    }
    const texture = new THREE.CanvasTexture(c);
    texture.encoding = THREE.sRGBEncoding;
    texture.anisotropy = 8;
    return texture;
  }

  #studioEnv() {
    const THREE = window.THREE;
    const env = new THREE.Scene();
    const disposables = [];
    const addMesh = (geometry, material) => {
      disposables.push(geometry, material);
      const mesh = new THREE.Mesh(geometry, material);
      env.add(mesh);
      return mesh;
    };
    addMesh(new THREE.BoxGeometry(12, 12, 12), new THREE.MeshBasicMaterial({ color: new THREE.Color(0.03, 0.024, 0.019), side: THREE.BackSide }));
    const panel = (w, h, pos, col) => {
      const mesh = addMesh(
        new THREE.PlaneGeometry(w, h),
        new THREE.MeshBasicMaterial({ color: new THREE.Color(col[0], col[1], col[2]), side: THREE.DoubleSide })
      );
      mesh.position.set(pos[0], pos[1], pos[2]);
      mesh.lookAt(0, 0, 0);
    };
    panel(3.2, 2.2, [-2.8, 3.4, 3.2], [5.2, 4.7, 4.1]);
    panel(3.4, 1.8, [0.2, 5.2, 0.4], [2.4, 2.3, 2.1]);
    panel(0.6, 3.4, [4.1, 1.6, -3.2], [4.0, 2.4, 1.3]);
    panel(0.6, 3.0, [-4.2, 1.4, -2.4], [2.8, 1.9, 1.0]);
    panel(0.25, 2.6, [-1.4, 1.2, 4.9], [3.2, 3.0, 2.7]);
    panel(0.25, 2.6, [1.7, 1.4, 4.7], [3.2, 3.0, 2.7]);
    panel(8, 8, [0, -5.5, 0], [0.11, 0.075, 0.05]);
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const texture = pmrem.fromScene(env, 0.035).texture;
    pmrem.dispose();
    for (const item of disposables) item.dispose();
    return texture;
  }

  #addLights() {
    const THREE = window.THREE;
    const key = new THREE.DirectionalLight('#FFF1DC', 1.35);
    key.position.set(-420, 720, 560);
    key.target.position.set(0, 40, 0);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    const sc = key.shadow.camera;
    sc.left = -300;
    sc.right = 300;
    sc.top = 300;
    sc.bottom = -300;
    sc.near = 200;
    sc.far = 2400;
    sc.updateProjectionMatrix();
    key.shadow.bias = -0.0004;
    key.shadow.normalBias = 0.8;
    const rim = new THREE.SpotLight('#FFB46E', 1.1, 0, 0.55, 0.85, 1);
    rim.position.set(420, 320, -560);
    rim.target.position.set(0, 50, 0);
    this.scene.add(key, key.target, rim, rim.target, new THREE.HemisphereLight('#FFF4E4', '#2A1C10', 0.32));
  }

  #bindPointer() {
    const THREE = window.THREE;
    const { signal } = this.#abortController;
    const canvas = this.canvas;
    const ray = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    /** @type {{ x: number, y: number, t: number } | null} */
    let down = null;
    /** @type {PointerEvent | null} */
    let lastMove = null;
    let hoverQueued = false;

    const pick = (event) => {
      const rect = canvas.getBoundingClientRect();
      ndc.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
      ray.setFromCamera(ndc, this.camera);
      const hits = ray.intersectObjects((this.proxies ?? []).concat(this.occluders ?? []), false);
      return hits[0] ?? null;
    };

    canvas.addEventListener(
      'pointerdown',
      (event) => {
        down = { x: event.clientX, y: event.clientY, t: performance.now() };
      },
      { signal }
    );
    canvas.addEventListener(
      'pointerup',
      (event) => {
        if (!down) return;
        const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
        const elapsed = performance.now() - down.t;
        down = null;
        if (moved > 6 || elapsed > 500) return;
        const hit = pick(event);
        if (!hit) return;
        const cartridge = hit.object.userData.cart;
        if (cartridge) {
          this.#stopSpin();
          this.#whenOpen(() => this.#fly(cartridge, cartridge.out ? 0 : 1));
        } else if (!this.lidOpen && !this.lidBusy) {
          this.#setLid(true);
        }
      },
      { signal }
    );
    canvas.addEventListener(
      'pointermove',
      (event) => {
        if (event.buttons) return;
        lastMove = event;
        if (hoverQueued) return;
        hoverQueued = true;
        requestAnimationFrame(() => {
          hoverQueued = false;
          if (!lastMove) return;
          const hit = pick(lastMove);
          canvas.style.cursor = hit && (hit.object.userData.cart || !this.lidOpen) ? 'pointer' : '';
        });
      },
      { signal }
    );
  }

  #resize() {
    const w = this.viewport.clientWidth;
    const h = this.viewport.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.#needsRender = true;
    this.#requestFrame();
  }

  #fitD(d) {
    const aspect = this.camera.aspect || 1.5;
    const size = Math.max(W, D * 1.5, 200) / 200;
    const distance = d * size;
    return aspect < 1.3 ? distance * Math.pow(1.3 / aspect, 0.85) : distance;
  }

  #getSph() {
    const o = this.camera.position.clone().sub(this.controls.target);
    const d = o.length();
    return { az: Math.atan2(o.x, o.z), el: Math.asin(o.y / d), d };
  }

  #setSph(az, el, d) {
    const t = this.controls.target;
    this.camera.position.set(t.x + d * Math.cos(el) * Math.sin(az), t.y + d * Math.sin(el), t.z + d * Math.cos(el) * Math.cos(az));
    this.camera.lookAt(t);
    this.#needsRender = true;
    this.#requestFrame();
  }

  #tween(dur, fn, ease = easeIO, done, delay = 0) {
    this.#tweens.push({ start: performance.now() + delay, dur: Math.max(dur, 1), fn, ease, done });
    this.#needsRender = true;
    this.#requestFrame();
  }

  #stepTweens(now) {
    if (!this.#tweens.length) return false;
    for (let i = this.#tweens.length - 1; i >= 0; i--) {
      const tw = this.#tweens[i];
      if (!tw || now < tw.start) continue;
      const k = Math.min(1, (now - tw.start) / tw.dur);
      tw.fn(tw.ease(k));
      if (k >= 1) {
        this.#tweens.splice(i, 1);
        tw.done?.();
      }
    }
    return true;
  }

  #applyLid() {
    if (!this.lidPivot) return;
    this.lidPivot.rotation.x = -rad(this.#lid.angle);
    for (const pivot of this.claspPivots) pivot.rotation.x = -rad(this.#lid.clasp);
    // A hanging padlock would block the lid, so it is only shown while the box is closed
    if (this.padlock && this.padlockHanging) this.padlock.visible = this.#lid.angle < 2 && this.#lid.clasp < 2;
    this.#needsRender = true;
  }

  #animLid(prop, to, dur, done) {
    const from = this.#lid[prop];
    this.#tween(dur * this.#speed, (k) => {
      this.#lid[prop] = from + (to - from) * k;
      this.#applyLid();
    }, easeIO, done);
  }

  #setLid(open) {
    if (this.lidBusy || open === this.lidOpen) return;
    if (!open && this.#flying > 0) return;
    this.lidBusy = true;
    this.#onStateChange();
    if (open) {
      this.#animLid('clasp', 100, 360, () =>
        this.#animLid('angle', 104, 950, () => {
          this.lidBusy = false;
          this.lidOpen = true;
          this.#onStateChange();
          while (this.#queue.length) this.#queue.shift()?.();
        })
      );
    } else {
      this.#animLid('angle', 0, 850, () =>
        this.#animLid('clasp', 0, 360, () => {
          this.lidBusy = false;
          this.lidOpen = false;
          this.#onStateChange();
          if (this.#queue.length) this.#setLid(true);
        })
      );
    }
  }

  #whenOpen(fn) {
    if (this.lidOpen && !this.lidBusy) {
      fn();
      return;
    }
    this.#queue.push(fn);
    if (!this.lidOpen && !this.lidBusy) this.#setLid(true);
  }

  #poseCart(c) {
    const { t, inP: A, outP: B } = c;
    let x;
    let y;
    let z;
    let rx;
    let k;
    if (t <= 0.3) {
      k = easeIO(t / 0.3);
      x = A.x;
      z = A.z;
      y = A.y + (LIFT - A.y) * k;
      rx = Math.PI;
    } else if (t < 0.75) {
      k = easeIO((t - 0.3) / 0.45);
      x = A.x + (B.x - A.x) * k;
      z = A.z + (B.z - A.z) * k;
      y = LIFT + Math.sin(k * Math.PI) * 28;
      rx = Math.PI * (1 + k);
    } else {
      k = easeOut((t - 0.75) / 0.25);
      x = B.x;
      z = B.z;
      y = LIFT + (B.y - LIFT) * k;
      rx = Math.PI * 2;
    }
    c.g.position.set(x, y, z);
    c.g.rotation.set(rx, 0, 0);
  }

  #fly(c, target, delay = 0) {
    if (c.busy || (target === 1) === c.out) return;
    c.busy = true;
    this.#flying++;
    this.#onStateChange();
    const from = c.t;
    this.#tween(
      1150 * this.#speed,
      (k) => {
        c.t = from + (target - from) * k;
        this.#poseCart(c);
      },
      lin,
      () => {
        c.busy = false;
        this.#flying--;
        c.out = target === 1;
        this.#onStateChange();
      },
      delay
    );
  }

  #setSpin(on) {
    this.spinning = on;
    this.controls.autoRotate = on;
    this.#needsRender = true;
    this.#requestFrame();
  }

  #stopSpin() {
    if (this.spinning) {
      this.#setSpin(false);
      this.#onStateChange();
    }
  }

  #requestFrame() {
    if (this.#frame || !this.#visible) return;
    this.#frame = requestAnimationFrame((now) => this.#loop(now));
  }

  #loop(now) {
    this.#frame = 0;
    const animating = this.#stepTweens(now);
    const moving = this.controls.update();
    if (animating || moving || this.#needsRender) {
      this.renderer.render(this.scene, this.camera);
      this.#needsRender = false;
    }
    // Keep looping while something is moving; damping and auto rotation need continuous updates
    if (animating || moving || this.spinning || this.#tweens.length) this.#requestFrame();
  }
}

/* ------------------------------------------------------------------------------------------------
 * Flag field: a searchable list of flags over the native select.
 * ---------------------------------------------------------------------------------------------- */

/** How long the flag style, size and position fields take to unfold, in line with blocks/caja-bandera.liquid. */
const UNFOLD_MS = 300;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {string} text
 * @returns {string} Lower case without accents, so "espana" finds "España".
 */
function searchKey(text) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** @type {WeakMap<HTMLElement, number>} */
const unfoldTimers = new WeakMap();

/**
 * Shows or hides a field by unfolding or folding its height.
 * @param {HTMLElement} element
 * @param {boolean} shown
 * @param {boolean} instant
 */
function unfold(element, shown, instant) {
  clearTimeout(unfoldTimers.get(element));
  const finish = () => {
    element.removeAttribute('data-unfolding');
    element.style.removeProperty('max-height');
    element.style.removeProperty('opacity');
    element.hidden = !shown;
  };
  if (instant || REDUCED_MOTION.matches) {
    finish();
    return;
  }
  if (shown === !element.hidden && !element.hasAttribute('data-unfolding')) return;

  element.hidden = false;
  element.setAttribute('data-unfolding', '');
  const full = `${element.scrollHeight}px`;
  element.style.maxHeight = shown ? '0px' : full;
  element.style.opacity = shown ? '0' : '1';
  // Start from the current size before moving to the new one
  void element.offsetHeight;
  element.style.maxHeight = shown ? full : '0px';
  element.style.opacity = shown ? '1' : '0';
  unfoldTimers.set(element, setTimeout(finish, UNFOLD_MS + 20));
}

/**
 * @typedef {object} CajaBanderaRefs
 * @property {HTMLLabelElement} label - Heading of the field.
 * @property {HTMLSelectElement} select - The Flag field sent with the order.
 * @property {HTMLElement} combo - Wraps the search box and the list.
 * @property {HTMLImageElement} current - Small flag of the choice, inside the search box.
 * @property {HTMLInputElement} input - The search box (combobox).
 * @property {HTMLElement} list - The list of flags (listbox).
 */

/**
 * Searchable list of flags over the native Flag select. Typing filters by name, ignoring accents;
 * each entry shows its flag, grouped as in the select. The choice is written back to the select,
 * which the 3D preview and the color rules read, and the flag style, size and position fields only
 * show once a flag is chosen.
 * @extends Component<CajaBanderaRefs>
 */
class CajaBandera extends Component {
  requiredRefs = ['label', 'select', 'combo', 'current', 'input', 'list'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** List entries, in the order of the select options. @type {HTMLElement[]} */
  #entries = [];
  /** @type {HTMLElement | null} */
  #empty = null;
  /** @type {HTMLElement | null} */
  #active = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { label, select, input, list } = this.refs;
    // Pressing an entry keeps the focus in the search box, so the list stays open until the click
    list.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
    list.addEventListener('click', (event) => this.#choose(event), { signal });
    // The search box takes over the label and the keyboard from the select it stands in for
    label.htmlFor = input.id;
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    // A rule may undo a choice by changing the select; show what it holds
    select.addEventListener('change', () => this.#sync(false), { signal });
    this.#sync(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  openList() {
    const { list, input } = this.refs;
    if (!list.hidden) return;
    this.#build();
    this.#render('');
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    input.select();
    this.#activate(this.#entries.find((entry) => entry.getAttribute('aria-selected') === 'true') ?? null);
  }

  filter() {
    if (this.refs.list.hidden) this.openList();
    this.#render(this.refs.input.value);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKey(event) {
    const open = !this.refs.list.hidden;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        this.openList();
        return;
      }
      this.#move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      if (this.#active) this.#pick(this.#active.dataset.value ?? '');
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      this.#close();
    } else if (event.key === 'Tab' && open) {
      this.#close();
    }
  }

  handleBlur() {
    if (!this.refs.list.hidden) this.#close();
  }

  /**
   * @param {MouseEvent} event
   */
  #choose(event) {
    const entry = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
    if (entry instanceof HTMLElement) this.#pick(entry.dataset.value ?? '');
  }

  /**
   * Builds the list once from the select: one entry per option, in groups like its optgroups.
   */
  #build() {
    if (this.#entries.length) return;
    const { list, select } = this.refs;
    /** @param {HTMLOptionElement} option */
    const entry = (option) => {
      const element = document.createElement('div');
      element.className = 'caja-bandera__option';
      element.id = `${list.id}-${this.#entries.length}`;
      element.setAttribute('role', 'option');
      element.dataset.value = option.value;
      element.dataset.search = searchKey(option.dataset.label ?? option.text);
      if (option.dataset.flagSrc) {
        const image = document.createElement('img');
        image.src = option.dataset.flagSrc;
        image.alt = '';
        image.width = 28;
        image.height = 21;
        image.loading = 'lazy';
        image.decoding = 'async';
        element.append(image);
      }
      element.append(document.createElement('span'));
      this.#entries.push(element);
      return element;
    };

    for (const child of select.children) {
      if (child instanceof HTMLOptionElement) {
        list.append(entry(child));
      } else if (child instanceof HTMLOptGroupElement) {
        const group = document.createElement('div');
        const heading = document.createElement('div');
        heading.id = `${list.id}-group-${list.children.length}`;
        heading.className = 'caja-bandera__group';
        heading.textContent = child.label;
        group.setAttribute('role', 'group');
        group.setAttribute('aria-labelledby', heading.id);
        group.append(heading);
        for (const option of child.children) {
          if (option instanceof HTMLOptionElement) group.append(entry(option));
        }
        list.append(group);
      }
    }
    this.#empty = document.createElement('p');
    this.#empty.className = 'caja-bandera__empty';
    this.#empty.textContent = this.dataset.noResults ?? '';
    this.#empty.hidden = true;
    list.append(this.#empty);
  }

  /**
   * Shows the entries that match the search, with their current labels and color limit marks.
   * @param {string} query
   */
  #render(query) {
    const words = searchKey(query.trim()).split(/\s+/).filter(Boolean);
    const { select, list } = this.refs;
    this.#entries.forEach((entry, index) => {
      // Entries were built in the order of the options
      const option = select.options[index];
      const label = entry.querySelector('span');
      if (option && label && label.textContent !== option.text) label.textContent = option.text;
      entry.toggleAttribute('data-color-limit', Boolean(option?.hasAttribute('data-color-limit')));
      entry.setAttribute('aria-selected', String(index === select.selectedIndex));
      entry.hidden = !words.every((word) => (entry.dataset.search ?? '').includes(word));
    });
    for (const group of list.querySelectorAll('[role="group"]')) {
      if (group instanceof HTMLElement) group.hidden = !group.querySelector('[role="option"]:not([hidden])');
    }
    const visible = this.#entries.filter((entry) => !entry.hidden);
    if (this.#empty) this.#empty.hidden = visible.length > 0;
    if (words.length) this.#activate(visible[0] ?? null);
  }

  /**
   * @param {1 | -1} step
   */
  #move(step) {
    const visible = this.#entries.filter((entry) => !entry.hidden);
    if (!visible.length) return;
    const index = this.#active ? visible.indexOf(this.#active) : -1;
    this.#activate(visible[Math.min(Math.max(index + step, 0), visible.length - 1)] ?? null);
  }

  /**
   * @param {HTMLElement | null} entry
   */
  #activate(entry) {
    this.#active?.removeAttribute('data-active');
    this.#active = entry;
    const { input } = this.refs;
    if (!entry) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    entry.setAttribute('data-active', '');
    input.setAttribute('aria-activedescendant', entry.id);
    entry.scrollIntoView({ block: 'nearest' });
  }

  /**
   * @param {string} value
   */
  #pick(value) {
    const { select } = this.refs;
    this.#close();
    if (select.value === value) return;
    select.value = value;
    // Bubbles to the 3D preview, the color rules and the configurator summary
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #close() {
    const { list, input } = this.refs;
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    this.#activate(null);
    this.#showChoice();
  }

  /**
   * @param {boolean} instant
   */
  #sync(instant) {
    this.#showChoice();
    const shown = Boolean(this.refs.select.value);
    const form = getForm(this.dataset.formId);
    for (const key of FLAG_DETAIL_KEYS) {
      const controls = getControls(form, key);
      for (const control of controls) control.disabled = !shown;
      const field = controls[0]?.closest('product-custom-property-component');
      if (field instanceof HTMLElement) unfold(field, shown, instant);
    }
  }

  #showChoice() {
    const { select, input, current } = this.refs;
    const option = select.selectedOptions[0];
    input.value = option ? (option.dataset.label ?? option.text) : '';
    const src = option?.dataset.flagSrc ?? '';
    if (src && current.getAttribute('src') !== src) current.src = src;
    current.hidden = !src;
  }
}

// Registered last: connecting runs the rules immediately, and they use the geometry constants above
customElements.define('caja-3d-viewer', Caja3dViewer);
customElements.define('caja-color-limit', CajaColorLimit);
customElements.define('caja-bandera', CajaBandera);
